import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { HydratedPost, PageState, UserData, Attachment, MediaType } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { FormattedText } from './FormattedText';
import { TiktokEmbed } from './PostCard';
import { BumpedPostContent } from './BumpedPostContent';
import { canViewPost, handleSliderPointerDown } from '../utils/helpers';
import { useScrollLock } from '../hooks/useScrollLock';
import { CustomAudioPlayer, CustomAudioPlayerHandle, AudioRecorder, AudioRecorderHandle } from './AudioComponents';
import api, { fetchCommunityDetails, API_URL } from '../api';
import { STORE_ITEMS } from '../constants';
import { useMentionSearch } from '../hooks/useMentionSearch';
import { MentionDropdown } from './MentionDropdown';
import { FONT_OPTIONS } from '../constants';
import { Capacitor } from '@capacitor/core';
import { Camera } from '@capacitor/camera';
import { NativeCamera } from './NativeCamera';
import { EmojiPickerModal } from './EmojiPickerModal';
import { GifPickerModal } from './GifPickerModal';
import { requestMicrophonePermission } from '../utils/permissionUtils';
import { compressImage, compressVideo, compressAudio, uploadMediaFile, downloadMediaUrl } from '../utils/mediaUtils';
import {
    LikeIcon,
    CheckmarkIcon,
    BumpIcon,
    SendIcon,
    BookmarkIcon,
    CommentIcon,
    TrashIcon,
    EditIcon,
    CloseIcon,
    MoreHorizontalIcon,
    VolumeOffIcon,
    BrokenHeartIcon,
    PrivateIcon,
    EyeIcon,
    EyeOffIcon,
    PlayIcon,
    PauseIcon,
    VolumeHighIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    GlobeIcon,
    ExternalLinkIcon,
    GoodieWhiteIcon,
    BellIcon,
    FlagIcon,
    MailIcon,
    UsersIcon,
    ReplyIcon,
    CopyIcon,
    DownloadIcon,
    PlusIcon,
    CameraIcon,
    PaperclipIcon,
    VideoIcon,
    MicIcon,
    EmojiIcon,
    SparklesIcon,
    SearchIcon
} from './Icons';
import { stickerTextStyle } from './StickerText';
import { getModBadgesForPost } from '../utils/ModRegistry';
import { ChatBubble } from './ChatBubble';
import { FacebookEmbed } from './PostCard';

const hexToRgba = (hex: string, opacity: number) => {
    try {
        let h = hex.replace('#', '');
        if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
        const r = parseInt(h.substring(0, 2), 16);
        const g = parseInt(h.substring(2, 4), 16);
        const b = parseInt(h.substring(4, 6), 16);
        return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    } catch {
        return null;
    }
};

interface PostModalProps {
    post: HydratedPost | null;
    onClose: () => void;
    onLike: (postId: number) => void;
    onPostClick?: (post: HydratedPost) => void;
    onComment: (postId: number, commentText: string, parentId?: number, media?: Attachment[]) => void;
    onBump: (post: HydratedPost) => void;
    onBookmark: (postId: number) => void;
    onVouch?: (postId: number) => void;
    onEdit: (post: HydratedPost) => void;
    onDelete: (postId: number, post?: HydratedPost) => void;
    onArchive?: (postId: number) => void;
    onUnarchive?: (postId: number) => void;
    onDeleteComment?: (postId: number, commentId: number) => void;
    onEditComment?: (postId: number, commentId: number, newText: string) => void;
    onCommentReaction?: (postId: number, commentId: number, emoji: string) => void;
    onToggleVisibility?: (postId: number) => void;
    onToggleCaption?: (postId: number) => void;
    onUpdateTextSize?: (postId: number, textSize: 'xs' | 'sm' | 'med' | 'lg') => void;
    onNavigateToProfile: (userId: string) => void;
    onNavigateToHashtag: (tag: string) => void;
    onShare?: (post: HydratedPost) => void;
    onMuteUser?: (userId: string) => void;
    onBlockUser?: (userId: string) => void;
    onReportPost?: (postId: number) => void;
    onGrantAdmin?: (userId: string) => void;
    onDeleteUser?: (userId: string) => void;
    onNavigateToCommunity?: (communityId: string, channelId?: string) => void;
    onUpdatePostcardStyle?: (postId: number, styleId: string) => void;
    onUpdatePostcardLayout?: (postId: number, layout: 'square' | 'dynamic') => void;
    isVisible: boolean;
    animatingLikes: Set<number>;
    animatingBumps: Set<number>;
    animatingComments: Set<number>;
    animatingBookmarks: Set<number>;
    setPage: (page: PageState) => void;
    communityName?: string;
    isFromLink?: boolean;
    onSwipePost?: (direction: 'left' | 'right', currentPostId: number) => void;
    onRespondLinkRequest?: (postId: number, response: 'down' | 'cant_go') => void;
}

const formatTime = (secs: number) => {
    if (isNaN(secs) || !isFinite(secs)) return '00:00';
    const m = Math.floor(secs / 60).toString().padStart(2, '0');
    const s = Math.floor(secs % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
};

const LinkRequestRSVPSection = ({
    post,
    currentUser,
    usersData,
    postThemeColor,
    onRespondLinkRequest
}: {
    post: HydratedPost;
    currentUser: string | null;
    usersData: Record<string, UserData>;
    postThemeColor?: string;
    onRespondLinkRequest?: (postId: number, response: 'down' | 'cant_go') => void;
}) => {
    const responses = post.linkRequestDetails?.responses || {};
    const currentUserResponse = currentUser ? responses[currentUser] : undefined;

    const downUserIds = Object.keys(responses).filter(u => responses[u] === 'down');
    const cantGoUserIds = Object.keys(responses).filter(u => responses[u] === 'cant_go');

    const location = post.linkRequestDetails?.location;
    const mapsUrl = location && location.lat !== undefined && location.lng !== undefined
        ? `https://www.google.com/maps/search/?api=1&query=${location.lat},${location.lng}`
        : null;

    const eventDate = post.linkRequestDetails?.eventDate;
    const eventTime = post.linkRequestDetails?.eventTime;

    const formattedDateTime = (() => {
        if (!eventDate && !eventTime) return null;
        let dateStr = '';
        if (eventDate) {
            const parts = eventDate.split('-');
            if (parts.length === 3) {
                const year = parseInt(parts[0], 10);
                const month = parseInt(parts[1], 10) - 1;
                const day = parseInt(parts[2], 10);
                const d = new Date(year, month, day);
                dateStr = d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
            } else {
                dateStr = eventDate;
            }
        }
        let timeStr = '';
        if (eventTime) {
            const parts = eventTime.split(':');
            if (parts.length >= 2) {
                let hour = parseInt(parts[0], 10);
                const min = parts[1];
                const ampm = hour >= 12 ? 'PM' : 'AM';
                hour = hour % 12 || 12;
                timeStr = `${hour}:${min} ${ampm}`;
            } else {
                timeStr = eventTime;
            }
        }
        return [dateStr, timeStr].filter(Boolean).join(' @ ');
    })();

    return (
        <div className="p-2 md:p-3 flex flex-col items-center gap-3 w-full max-w-md mx-auto select-none">
            <div className="text-center flex flex-col items-center gap-1">
                <h4 className="text-sm md:text-base font-black uppercase tracking-wider text-white" style={{ color: postThemeColor, ...stickerTextStyle(0.18) }}>
                    Link Request
                </h4>
                <p className="text-xs opacity-75 text-white/80" style={stickerTextStyle(0.15)}>
                    Let @{post.author} know if you can link up!
                </p>
                <div className="flex flex-wrap items-center justify-center gap-2 mt-1">
                    {mapsUrl && (
                        <a
                            href={mapsUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600/40 hover:bg-blue-600/60 border border-blue-400/40 rounded-full text-white text-[11px] font-bold transition-all transform hover:scale-105 active:scale-95 shadow-lg"
                            style={stickerTextStyle(0.15)}
                        >
                            <span>📍</span>
                            <span>Open Location in Maps</span>
                            <ExternalLinkIcon className="w-3 h-3" />
                        </a>
                    )}
                    {formattedDateTime && (
                        <div
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-500/30 border border-amber-400/40 rounded-full text-amber-200 text-[11px] font-bold shadow-lg"
                            style={stickerTextStyle(0.15)}
                        >
                            <span>📅</span>
                            <span>{formattedDateTime}</span>
                        </div>
                    )}
                </div>
            </div>

            {/* Response Lists acting as Interactive RSVP Buttons */}
            <div className="w-full space-y-2.5">
                {/* Down users list button */}
                <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onRespondLinkRequest?.(post.id, 'down'); }}
                    className={`w-full p-3 rounded-2xl flex items-center justify-between transition-all duration-200 border cursor-pointer active:scale-[0.98] ${
                        currentUserResponse === 'down'
                            ? 'bg-green-500/35 border-green-400/80 shadow-lg shadow-green-500/25 ring-2 ring-green-400/40'
                            : 'bg-black/30 hover:bg-black/50 border-white/10'
                    }`}
                >
                    <div className="flex items-center gap-2">
                        <span className="text-sm">📍</span>
                        <span className="text-xs font-black text-green-400 uppercase tracking-wider" style={stickerTextStyle(0.15)}>
                            I'm Down ({downUserIds.length})
                        </span>
                    </div>
                    <div className="flex items-center -space-x-2 overflow-hidden">
                        {downUserIds.length === 0 ? (
                            <span className="text-[11px] text-gray-400 italic">Tap to RSVP</span>
                        ) : (
                            downUserIds.map(uId => (
                                <img
                                    key={uId}
                                    src={usersData[uId]?.pfp || `https://api.dicebear.com/7.x/bottts/svg?seed=${uId}`}
                                    alt={uId}
                                    title={usersData[uId]?.displayName || uId}
                                    className="w-6 h-6 rounded-full border-2 border-gray-900 object-cover"
                                />
                            ))
                        )}
                    </div>
                </button>

                {/* Cant go users list button */}
                <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onRespondLinkRequest?.(post.id, 'cant_go'); }}
                    className={`w-full p-3 rounded-2xl flex items-center justify-between transition-all duration-200 border cursor-pointer active:scale-[0.98] ${
                        currentUserResponse === 'cant_go'
                            ? 'bg-red-500/35 border-red-400/80 shadow-lg shadow-red-500/25 ring-2 ring-red-400/40'
                            : 'bg-black/30 hover:bg-black/50 border-white/10'
                    }`}
                >
                    <div className="flex items-center gap-2">
                        <span className="text-sm">😅</span>
                        <span className="text-xs font-black text-red-400 uppercase tracking-wider" style={stickerTextStyle(0.15)}>
                            Can't Go ({cantGoUserIds.length})
                        </span>
                    </div>
                    <div className="flex items-center -space-x-2 overflow-hidden">
                        {cantGoUserIds.length === 0 ? (
                            <span className="text-[11px] text-gray-400 italic">Tap to RSVP</span>
                        ) : (
                            cantGoUserIds.map(uId => (
                                <img
                                    key={uId}
                                    src={usersData[uId]?.pfp || `https://api.dicebear.com/7.x/bottts/svg?seed=${uId}`}
                                    alt={uId}
                                    title={usersData[uId]?.displayName || uId}
                                    className="w-6 h-6 rounded-full border-2 border-gray-900 object-cover"
                                />
                            ))
                        )}
                    </div>
                </button>
            </div>
        </div>
    );
};


export const PostModal = ({
    post,
    onClose,
    onLike,
    onPostClick,
    onComment,
    onBump,
    onBookmark,
    onVouch,
    onEdit,
    onDelete,
    onArchive,
    onUnarchive,
    onDeleteComment,
    onEditComment,
    onCommentReaction,
    onToggleVisibility,
    onToggleCaption,
    onUpdateTextSize,
    onNavigateToProfile,
    onNavigateToHashtag,
    onShare,
    onMuteUser,
    onBlockUser,
    onReportPost,
    onGrantAdmin,
    onDeleteUser,
    onNavigateToCommunity,
    onUpdatePostcardStyle,
    onUpdatePostcardLayout,
    isVisible,
    animatingLikes,
    animatingBumps,
    animatingComments,
    animatingBookmarks,
    setPage,
    communityName,
    isFromLink,
    onSwipePost,
    onRespondLinkRequest
}: PostModalProps) => {
    const { currentUser, usersData, communities } = useAuth();
    const getCleanUserKey = (username: string | undefined): string | null => {
        if (!username) return null;
        let clean = username.trim();
        if (clean.startsWith('@')) {
            clean = clean.slice(1);
        }
        const lower = clean.toLowerCase();
        return Object.keys(usersData).find(k => k.toLowerCase() === lower || k.toLowerCase().replace(/\./g, '_') === lower.replace(/\./g, '_')) || clean;
    };
    const [isOptionsOpen, setIsOptionsOpen] = useState(false);
    const optionsRef = useRef<HTMLDivElement>(null);
    const [newComment, setNewComment] = useState("");
    const [attachments, setAttachments] = useState<Attachment[]>([]);
    const [isUploadingMedia, setIsUploadingMedia] = useState(false);
    const [showMessageRecorder, setShowMessageRecorder] = useState(false);
    const [audioStream, setAudioStream] = useState<MediaStream | null>(null);
    const [showTenorModal, setShowTenorModal] = useState(false);
    const [showInputMenu, setShowInputMenu] = useState(false);
    const [showCamera, setShowCamera] = useState(false);
    const [cameraMode, setCameraMode] = useState<'image' | 'video' | 'both'>('both');
    const [showEmojiPickerFor, setShowEmojiPickerFor] = useState<string | null>(null);
    const audioRecorderRef = useRef<AudioRecorderHandle>(null);
    const commentFileInputRef = useRef<HTMLInputElement>(null);
    const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
    const [touchStart, setTouchStart] = useState(0);
    const [commentToDelete, setCommentToDelete] = useState<number | null>(null);
    const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
    const [replyTo, setReplyTo] = useState<Comment | null>(null);
    const [activeCommentMenu, setActiveCommentMenu] = useState<any | null>(null);
    const [editingComment, setEditingComment] = useState<any | null>(null);
    const commentTouchStartPos = useRef<{ x: number, y: number }>({ x: 0, y: 0 });
    const commentLongPressTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const [resolvedTiktokVideoId, setResolvedTiktokVideoId] = useState("");
    const [tiktokDirectVideoUrl, setTiktokDirectVideoUrl] = useState<string | null>(null);
    const [isTiktokLoading, setIsTiktokLoading] = useState(false);
    const [mediaErrors, setMediaErrors] = useState<Record<string, boolean>>({});
    const [reloadKeys, setReloadKeys] = useState<Record<string, number>>({});
    const handleMediaReload = (url: string) => {
        setReloadKeys(prev => ({ ...prev, [url]: (prev[url] || 0) + 1 }));
        setMediaErrors(prev => ({ ...prev, [url]: false }));
    };
    const [copiedText, setCopiedText] = useState(false);
    const handleCopyCaption = (text: string) => {
        navigator.clipboard.writeText(text).then(() => {
            setCopiedText(true);
            setTimeout(() => setCopiedText(false), 2000);
        }).catch(err => console.error('Failed to copy text:', err));
    };
    const [zoomScale, setZoomScale] = useState(1);
    const [zoomPosition, setZoomPosition] = useState({ x: 0, y: 0 });
    const isZoomMode = zoomScale > 1;

    const initialDistanceRef = useRef<number | null>(null);
    const initialScaleRef = useRef<number>(1);
    const zoomStartPosRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
    const isDraggingZoomRef = useRef<boolean>(false);

    useEffect(() => {
        setZoomScale(1);
        setZoomPosition({ x: 0, y: 0 });
    }, [currentMediaIndex, post?.id]);

    const handleZoomTouchStart = (e: React.TouchEvent) => {
        if (e.touches.length === 2) {
            e.stopPropagation();
            const dist = Math.hypot(
                e.touches[0].clientX - e.touches[1].clientX,
                e.touches[0].clientY - e.touches[1].clientY
            );
            initialDistanceRef.current = dist;
            initialScaleRef.current = zoomScale;
            isDraggingZoomRef.current = false;
        } else if (e.touches.length === 1 && zoomScale > 1) {
            e.stopPropagation();
            zoomStartPosRef.current = {
                x: e.touches[0].clientX - zoomPosition.x,
                y: e.touches[0].clientY - zoomPosition.y
            };
            isDraggingZoomRef.current = true;
        }
    };

    const handleZoomTouchMove = (e: React.TouchEvent) => {
        if (e.touches.length === 2 && initialDistanceRef.current !== null) {
            e.preventDefault();
            e.stopPropagation();
            const dist = Math.hypot(
                e.touches[0].clientX - e.touches[1].clientX,
                e.touches[0].clientY - e.touches[1].clientY
            );
            const nextScale = (dist / initialDistanceRef.current) * initialScaleRef.current;
            setZoomScale(Math.max(1, Math.min(nextScale, 4)));
        } else if (e.touches.length === 1 && isDraggingZoomRef.current && zoomScale > 1) {
            e.preventDefault();
            e.stopPropagation();
            const nextX = e.touches[0].clientX - zoomStartPosRef.current.x;
            const nextY = e.touches[0].clientY - zoomStartPosRef.current.y;
            setZoomPosition({ x: nextX, y: nextY });
        }
    };

    const handleZoomTouchEnd = (e: React.TouchEvent) => {
        e.stopPropagation();

        if (e.touches.length < 2) {
            initialDistanceRef.current = null;
        }
        if (e.touches.length === 0) {
            isDraggingZoomRef.current = false;
            if (zoomScale <= 1.05) {
                setZoomScale(1);
                setZoomPosition({ x: 0, y: 0 });
            }
        }
    };
    const autoReloadMedia = (url: string) => {
        const retries = reloadKeys[url] || 0;
        if (retries < 3) {
            setTimeout(() => {
                setReloadKeys(prev => ({ ...prev, [url]: (prev[url] || 0) + 1 }));
                setMediaErrors(prev => ({ ...prev, [url]: false }));
            }, 1000);
            return true;
        }
        return false;
    };

    const [touchEnd, setTouchEnd] = useState(0);
    const { mentionQuery, updateSearch, completeMention } = useMentionSearch();
    const videoRef = useRef<HTMLVideoElement>(null);
    const audioPlayerRef = useRef<CustomAudioPlayerHandle>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const lastTapTimeRef = useRef<number>(0);
    const tapTimeoutRef = useRef<number | null>(null);
    const longPressTimerRef = useRef<NodeJS.Timeout | null>(null);
    const isLongPressRef = useRef<boolean>(false);
    const lastStartEventTimeRef = useRef<number>(0);
    const lastEndEventTimeRef = useRef<number>(0);
    const hasMovedRef = useRef<boolean>(false);
    const touchStartPosRef = useRef<{ x: number, y: number }>({ x: 0, y: 0 });
    const modalSwipeStartX = useRef<number | null>(null);
    const modalSwipeStartY = useRef<number | null>(null);

    const [modalSwipeDirection, setModalSwipeDirection] = useState<'left' | 'right' | null>(null);

    const handleModalTouchStart = (e: React.TouchEvent) => {
        modalSwipeStartX.current = e.touches[0].clientX;
        modalSwipeStartY.current = e.touches[0].clientY;
    };

    const handleModalTouchEnd = (e: React.TouchEvent) => {
        if (modalSwipeStartX.current !== null && modalSwipeStartY.current !== null) {
            const currentX = e.changedTouches[0].clientX;
            const currentY = e.changedTouches[0].clientY;
            const deltaX = currentX - modalSwipeStartX.current;
            const deltaY = currentY - modalSwipeStartY.current;

            const isMediaCarousel = mediaItems.length > 1 && (e.target as HTMLElement).closest('.media-swipe-container');

            if (Math.abs(deltaX) > 200 && Math.abs(deltaX) > Math.abs(deltaY) && !isMediaCarousel) {
                if (onSwipePost && post) {
                    const direction = deltaX > 0 ? 'right' : 'left';
                    setModalSwipeDirection(direction);
                    onSwipePost(direction, post.id);
                }
            }
        }
        modalSwipeStartX.current = null;
        modalSwipeStartY.current = null;
    };
    const [isPlaying, setIsPlaying] = useState(true);
    const [isMuted, setIsMuted] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [showFullscreenControls, setShowFullscreenControls] = useState(false);
    const controlsTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const [modalReady, setModalReady] = useState(false);

    const formatRelativeTime = (dateString?: string) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now.getTime() - date.getTime();
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);

        if (diffMins < 10) return "Just now!";
        if (diffMins < 60) return `${diffMins} minutes ago`;
        if (diffHours < 24) return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;

        return date.toLocaleString([], {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit'
        });
    };

    const triggerControlsDisplay = useCallback(() => {
        setShowFullscreenControls(true);
        if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
        controlsTimeoutRef.current = setTimeout(() => setShowFullscreenControls(false), 3000);
    }, []);

    const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth <= 768);
    const [isPhone, setIsPhone] = useState(typeof window !== 'undefined' && window.innerWidth <= 1024);
    const [isMobileLandscape, setIsMobileLandscape] = useState(
        typeof window !== 'undefined' && window.innerWidth > window.innerHeight && window.innerHeight < 600
    );

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
            setIsPhone(window.innerWidth <= 1024);
            setIsMobileLandscape(window.innerWidth > window.innerHeight && window.innerHeight < 600);
        };
        window.addEventListener('resize', handleResize);
        window.addEventListener('orientationchange', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('orientationchange', handleResize);
        };
    }, []);

    const isMobilePortrait = isPhone && !isMobileLandscape;

    const currentUserData = usersData[currentUser || ''];
    const defaultBlurOpacity = currentUserData?.postBlurOpacity ?? 50;
    const enforceThemeColor = currentUserData?.enforceThemeColor ?? false;
    const enforceThemeOpacity = currentUserData?.enforceThemeOpacity ?? false;

    // Get the post to show and media items
    const postToShow = post?.type === 'bump' ? post?.originalPost : post;
    const isSimpleBump = useMemo(() => {
        return post?.type === 'bump' && (!post?.content.text || post?.content.text.trim() === '');
    }, [post?.type, post?.content.text]);
    const mediaItems = postToShow?.content.media?.filter(m => m.type === 'image' || m.type === 'video') || [];
    const hasMedia = mediaItems.length > 0 || Boolean(postToShow?.content?.coverImageUrl);

    const isSquareByDefault = useMemo(() => {
        if (!postToShow) return true;
        if (postToShow.type === 'text') return false;
        const firstMedia = mediaItems[0];
        if (firstMedia?.width && firstMedia?.height) {
            const ratio = firstMedia.width / firstMedia.height;
            return ratio >= 0.8 && ratio <= 1.2;
        }
        return true;
    }, [postToShow, mediaItems]);

    const postColorRgb = useMemo(() => {
        const themeToUse = isSimpleBump ? postToShow?.themeColor : (post?.themeColor || postToShow?.themeColor);
        const themeColor = enforceThemeColor ? currentUserData?.appThemeColor : (themeToUse || '#000000');
        if (!themeColor) return null;
        try {
            let hex = themeColor.replace('#', '');
            if (hex.length === 3) {
                hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
            }
            const r = parseInt(hex.substring(0, 2), 16);
            const g = parseInt(hex.substring(2, 4), 16);
            const b = parseInt(hex.substring(4, 6), 16);
            return isNaN(r) || isNaN(g) || isNaN(b) ? null : `${r}, ${g}, ${b}`;
        } catch (e) {
            return null;
        }
    }, [post?.themeColor, postToShow?.themeColor, isSimpleBump, enforceThemeColor, currentUserData]);

    const postTextColor = useMemo(() => {
        const currentUserData = usersData[currentUser || ''];
        if (currentUserData?.enforceTextColor) {
            return currentUserData.appTextColor || '#ffffff';
        }
        return isSimpleBump ? (postToShow?.textColor || '#ffffff') : (post?.textColor || postToShow?.textColor || '#ffffff');
    }, [post?.textColor, postToShow?.textColor, isSimpleBump, currentUser, usersData]);

    const postFontFamily = useMemo(() => {
        const fontId = post?.type === 'bump' ? postToShow?.fontId : (post?.fontId || postToShow?.fontId);
        if (!fontId || fontId === 'default' || fontId === 'font_default') return undefined;
        const font = FONT_OPTIONS.find(f => f.id === fontId);
        return font ? font.family : undefined;
    }, [post?.fontId, post?.type, postToShow?.fontId]);

    const modalBg = useMemo(() => {
        const opacityToUse = isSimpleBump ? (postToShow?.themeOpacity ?? 50) : (post?.themeOpacity ?? postToShow?.themeOpacity ?? 50);
        const activeOpacity = enforceThemeOpacity ? defaultBlurOpacity : opacityToUse;
        if (postColorRgb) {
            return `rgba(${postColorRgb}, ${activeOpacity / 100})`;
        }
        return 'var(--app-theme-bg)';
    }, [postColorRgb, defaultBlurOpacity, enforceThemeOpacity, post?.themeOpacity, postToShow?.themeOpacity, isSimpleBump]);

    const [feedbackIcon, setFeedbackIcon] = useState<'play' | 'pause' | 'mute' | 'unmute' | 'forward' | 'rewind' | '2x' | null>(null);
    const [progress, setProgress] = useState(0);
    const [localCommunityName, setLocalCommunityName] = useState<string | undefined>(communityName);

    // Always reset currentMediaIndex to 0 when post or isVisible changes
    useEffect(() => {
        setCurrentMediaIndex(0);
        setModalReady(false);
        if (isVisible) {
            const hasVideo = postToShow?.content.media?.some(m => m.type === 'video');
            if (hasVideo) {
                setIsFullscreen(true);
                triggerControlsDisplay();
            } else {
                setIsFullscreen(false);
            }

            const delay = isFromLink ? 50 : 1000;
            const timer = setTimeout(() => setModalReady(true), delay);
            return () => clearTimeout(timer);
        }
    }, [isVisible, postToShow?.id, isFromLink]);

    // Clamp currentMediaIndex if mediaItems length changes
    useEffect(() => {
        if (currentMediaIndex >= mediaItems.length) {
            setCurrentMediaIndex(0);
        }
    }, [mediaItems.length]);

    useEffect(() => {
        // prefer explicit prop when provided
        if (communityName) {
            setLocalCommunityName(communityName);
            return;
        }

        const cid = post?.communityId || postToShow?.communityId;
        if (!cid) {
            setLocalCommunityName(undefined);
            return;
        }

        let cancelled = false;
        fetchCommunityDetails(cid).then((data: any) => {
            if (cancelled) return;
            const name = data?.name || data?.title || cid;
            setLocalCommunityName(name);
        }).catch(() => {
            if (!cancelled) setLocalCommunityName(undefined);
        });

        return () => { cancelled = true; };
    }, [post?.communityId, postToShow?.communityId, communityName]);

    useEffect(() => {
        if (!isOptionsOpen) return;
        const handleOutsideClick = (e: MouseEvent) => {
            if (optionsRef.current && !optionsRef.current.contains(e.target as Node)) {
                setIsOptionsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleOutsideClick);
        return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, [isOptionsOpen]);

    useEffect(() => {
        const video = videoRef.current;
        if (isVisible && modalReady && video) {
            const currentMedia = mediaItems[currentMediaIndex];
            if (currentMedia?.type === 'video') {
                video.muted = isMuted;
                video.play().catch(error => {
                    console.log("Video autoplay prevented:", error);
                    // Fallback to muted autoplay if unmuted failed
                    if (!video.muted) {
                        video.muted = true;
                        video.play().catch(e => console.log("Muted autoplay also prevented:", e));
                    }
                });
            }
        }
        return () => {
            if (video) video.pause();
        };
    }, [isVisible, modalReady, currentMediaIndex, postToShow?.id, isMuted]);

    useEffect(() => {
        if (isVisible) {
            if (post?.type === 'link' && post.content.linkPreview?.embedHtml) {
                const domain = post.content.linkPreview?.domain;
                setTimeout(() => {
                    if (domain === 'instagram.com') {
                        if ((window as any).instgrm) {
                            (window as any).instgrm.Embeds.process();
                        }
                    }
                    if (domain === 'tiktok.com') {
                        if ((window as any).tiktok) {
                            try { (window as any).tiktok.load(); } catch (e) { }
                        }
                    }
                }, 500);
            }
        }
    }, [isVisible, post]);

    useEffect(() => {
        let active = true;
        let timeoutId: NodeJS.Timeout | null = null;

        if (isVisible && postToShow?.type === 'link' && postToShow.content.linkPreview?.domain === 'tiktok.com') {
            const linkPreview = postToShow.content.linkPreview;
            if (!linkPreview) return;
            const url = linkPreview.url || '';
            const tiktokRegex = /(?:https?:\/\/)?(?:www\.|vm\.|vt\.|v\.)?tiktok\.com\/.*\/video\/(\d+)/;

            const resolveTiktok = async () => {
                if (!active) return;
                setIsTiktokLoading(true);

                // Safe timeout to abort loading spinner if resolution takes too long
                timeoutId = setTimeout(() => {
                    if (active) {
                        setIsTiktokLoading(false);
                        console.warn("[TikTok Player] Resolution timed out, stopping spinner.");
                    }
                }, 8000);

                try {
                    const response = await api.post('metadata', { url });
                    if (response.data && response.data.url) {
                        const resolvedMatch = response.data.url.match(tiktokRegex);
                        if (resolvedMatch && resolvedMatch[1]) {
                            setResolvedTiktokVideoId(resolvedMatch[1]);
                        }
                    }
                } catch (e) {
                    console.error("[TikTok Player] Failed to resolve TikTok URL:", e);
                    if (active) {
                        const localMatch = url.match(tiktokRegex);
                        if (localMatch && localMatch[1]) {
                            setResolvedTiktokVideoId(localMatch[1]);
                        }
                    }
                } finally {
                    if (active) {
                        setIsTiktokLoading(false);
                        if (timeoutId) clearTimeout(timeoutId);
                    }
                }
            };

            const match = url.match(tiktokRegex) || (linkPreview.embedHtml || '').match(/data-video-id="(\d+)"/);
            if (match && match[1]) {
                setResolvedTiktokVideoId(match[1]);
                resolveTiktok();
            } else {
                resolveTiktok();
            }
        } else {
            setResolvedTiktokVideoId("");
            setTiktokDirectVideoUrl(null);
            setIsTiktokLoading(false);
        }

        return () => {
            active = false;
            if (timeoutId) clearTimeout(timeoutId);
        };
    }, [isVisible, postToShow?.id, postToShow?.content.linkPreview?.url]);

    const handleAttachTenorGif = (gifUrl: string) => {
        const existingIsAudio = attachments.length > 0 && (attachments[0].type === 'audio' || attachments[0].type === 'voice');
        if (existingIsAudio) {
            alert("You can't mix photos/videos with audio files in the same comment.");
            return;
        }
        if (attachments.length + 1 > 10) {
            alert("You can only upload up to 10 photos/videos per comment.");
            return;
        }
        setAttachments(prev => [...prev, { url: gifUrl, type: 'image' }]);
        setShowTenorModal(false);
        setShowInputMenu(false);
    };

    const handleCameraCapture = (file: File) => {
        handleMediaUploadFiles([file], true);
    };

    const handleGalleryClick = async () => {
        if (Capacitor.isNativePlatform()) {
            try {
                const checkStatus = await Camera.checkPermissions();
                if (checkStatus.photos !== 'granted' && checkStatus.photos !== 'limited') {
                    const reqStatus = await Camera.requestPermissions({ permissions: ['photos'] });
                    if (reqStatus.photos !== 'granted' && reqStatus.photos !== 'limited') {
                        throw new Error('Photo library permission denied');
                    }
                }
                const result = await Camera.pickImages({
                    quality: 100,
                    limit: 10
                });
                if (result.photos && result.photos.length > 0) {
                    const files = await Promise.all(result.photos.map(async (p, idx) => {
                        const response = await fetch(p.webPath!);
                        const blob = await response.blob();
                        const extension = p.format || 'jpeg';
                        const filename = `gallery-${Date.now()}-${idx}.${extension}`;
                        return new File([blob], filename, { type: blob.type || `image/${extension}` });
                    }));
                    handleMediaUploadFiles(files);
                }
            } catch (err) {
                console.error("Native gallery pick failed, falling back to file input", err);
                commentFileInputRef.current?.click();
            }
        } else {
            commentFileInputRef.current?.click();
        }
    };

    const handleVoiceMessageComplete = async (audioUrl: string, blob: Blob) => {
        try {
            let extension = 'webm';
            let mimeType = blob.type || 'audio/webm';
            if (blob.type) {
                if (blob.type.includes('mp4')) {
                    extension = 'm4a';
                    mimeType = 'audio/mp4';
                } else if (blob.type.includes('aac')) {
                    extension = 'aac';
                    mimeType = 'audio/aac';
                } else if (blob.type.includes('ogg')) {
                    extension = 'ogg';
                    mimeType = 'audio/ogg';
                } else if (blob.type.includes('wav')) {
                    extension = 'wav';
                    mimeType = 'audio/wav';
                } else if (blob.type.includes('webm')) {
                    extension = 'webm';
                    mimeType = 'audio/webm';
                }
            } else {
                const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
                if (isIOS) {
                    extension = 'm4a';
                    mimeType = 'audio/mp4';
                }
            }
            const file = new File([blob], `voice-message-${Date.now()}.${extension}`, { type: mimeType });
            const tempUrl = URL.createObjectURL(file);
            setAttachments(prev => [...prev, { url: tempUrl, type: 'voice', file: file }]);
            setShowMessageRecorder(false);
            if (audioStream) {
                audioStream.getTracks().forEach(track => track.stop());
                setAudioStream(null);
            }
        } catch (error) {
            console.error("Failed to process voice message", error);
        }
    };

    const handleVoiceMessageReset = () => {
        setShowMessageRecorder(false);
        if (audioStream) {
            audioStream.getTracks().forEach(track => track.stop());
            setAudioStream(null);
        }
    };

    const handleMediaUploadFiles = async (files: File[], isLive?: boolean) => {
        if (!files.length) return;

        const totalSize = files.reduce((sum, file) => sum + file.size, 0);
        if (totalSize > 500 * 1024 * 1024) {
            alert('File size exceeds the 500MB limit. Please choose a smaller file.');
            return;
        }

        setIsUploadingMedia(true);

        try {
            const processedAttachments = await Promise.all(files.map(async (file) => {
                let processedFile = file;
                let type: MediaType = 'file';
                const typeRaw = file.type.split('/')[0];
                if (typeRaw === 'video') type = 'video';
                else if (typeRaw === 'audio') type = 'audio';
                else if (typeRaw === 'image') type = 'image';

                if (type === 'video') {
                    const blob = await compressVideo(file);
                    processedFile = new File([blob], file.name, { type: file.type });
                } else if (type === 'audio') {
                    const blob = await compressAudio(file);
                    processedFile = new File([blob], file.name, { type: file.type });
                } else if (type === 'image') {
                    const blob = await compressImage(file);
                    processedFile = new File([blob], file.name, { type: file.type });
                }
                return { url: URL.createObjectURL(processedFile), type, file: processedFile, isLive };
            }));
            setAttachments(prev => [...prev, ...processedAttachments]);
        } catch (error) {
            console.error('Failed to prepare media:', error);
        } finally {
            setIsUploadingMedia(false);
        }
    };

    const handleMediaUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        await handleMediaUploadFiles(files);
        if (e.target) e.target.value = '';
    };

    const removeAttachment = (index: number) => {
        setAttachments(prev => prev.filter((_, i) => i !== index));
    };

    const handlePaste = async (e: React.ClipboardEvent) => {
        const items = e.clipboardData.items;
        const files: File[] = [];
        for (let i = 0; i < items.length; i++) {
            if (items[i].type.indexOf('image') !== -1 || items[i].type.indexOf('video') !== -1 || items[i].type.indexOf('audio') !== -1) {
                const file = items[i].getAsFile();
                if (file) files.push(file);
            }
        }
        if (files.length > 0) {
            handleMediaUploadFiles(files);
        }
    };

    const submitComment = async () => {
        if (post && (newComment.trim() || attachments.length > 0)) {
            setIsUploadingMedia(true);
            try {
                const uploadedAttachments = attachments.length > 0
                    ? await Promise.all(attachments.map(async (att) => {
                        if (att.file && currentUser) {
                            const url = await uploadMediaFile(att.file, currentUser);
                            return {
                                url,
                                type: att.type,
                                width: att.width,
                                height: att.height,
                                duration: att.duration,
                                isLive: att.isLive
                            };
                        }
                        return { url: att.url, type: att.type, width: att.width, height: att.height, duration: att.duration, isLive: att.isLive };
                    }))
                    : [];

                if (editingComment) {
                    if (onEditComment) {
                        onEditComment(post.id, editingComment.id, newComment.trim());
                    }
                    setEditingComment(null);
                } else {
                    onComment(post.id, newComment.trim(), (replyTo as any)?.id, uploadedAttachments);
                }
                setNewComment("");
                setReplyTo(null);
                setAttachments([]);
            } catch (err) {
                console.error("Failed to submit comment:", err);
                alert("Failed to upload comment attachments.");
            } finally {
                setIsUploadingMedia(false);
            }
        }
    };

    const handleCommentSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        submitComment();
    };

    const handleCommentTouchStart = (e: React.TouchEvent | React.MouseEvent, comment: any) => {
        const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
        const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
        commentTouchStartPos.current = { x: clientX, y: clientY };

        if (commentLongPressTimeoutRef.current) clearTimeout(commentLongPressTimeoutRef.current);
        commentLongPressTimeoutRef.current = setTimeout(() => {
            setActiveCommentMenu(comment);
        }, 600);
    };

    const handleCommentTouchMove = (e: React.TouchEvent | React.MouseEvent) => {
        const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
        const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
        const dx = clientX - commentTouchStartPos.current.x;
        const dy = clientY - commentTouchStartPos.current.y;
        if (Math.sqrt(dx * dx + dy * dy) > 10) {
            if (commentLongPressTimeoutRef.current) {
                clearTimeout(commentLongPressTimeoutRef.current);
                commentLongPressTimeoutRef.current = null;
            }
        }
    };

    const handleCommentTouchEnd = () => {
        if (commentLongPressTimeoutRef.current) {
            clearTimeout(commentLongPressTimeoutRef.current);
            commentLongPressTimeoutRef.current = null;
        }
    };

    const handleCommentContextMenu = (e: React.MouseEvent, comment: any) => {
        e.preventDefault();
        setActiveCommentMenu(comment);
    };

    const handleCommentKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            if (!isMobile) {
                e.preventDefault();
                submitComment();
            }
        }
    };

    const audioItems = useMemo(() => {
        const items = postToShow?.content.media?.filter((m: any) => m.type === 'audio' || m.type === 'voice') || [];
        const seen = new Set();
        return items.filter((item: any) => {
            if (!item.url || seen.has(item.url)) return false;
            seen.add(item.url);
            return true;
        });
    }, [postToShow?.content.media]);
    const nextMedia = () => { if (mediaItems.length) setCurrentMediaIndex(prev => (prev + 1) % mediaItems.length); };
    const prevMedia = () => { if (mediaItems.length) setCurrentMediaIndex(prev => (prev - 1 + mediaItems.length) % mediaItems.length); };

    if (!post || !isVisible || !postToShow) return null;

    const handlePressStart = (clientX: number, clientY: number) => {
        if (isZoomMode) return;
        const now = Date.now();
        if (now - lastStartEventTimeRef.current < 150) return;
        lastStartEventTimeRef.current = now;

        if (longPressTimerRef.current) {
            clearTimeout(longPressTimerRef.current);
            longPressTimerRef.current = null;
        }

        setTouchStart(clientX);
        touchStartPosRef.current = { x: clientX, y: clientY };
        hasMovedRef.current = false;
        isLongPressRef.current = false;

        longPressTimerRef.current = setTimeout(() => {
            if (!hasMovedRef.current && videoRef.current) {
                isLongPressRef.current = true;
                videoRef.current.playbackRate = 2.0;
                setFeedbackIcon('2x');
            }
        }, 300);

        const handleMove = (e: TouchEvent | MouseEvent) => {
            const x = e instanceof TouchEvent ? e.touches[0].clientX : e.clientX;
            const y = e instanceof TouchEvent ? e.touches[0].clientY : e.clientY;
            const deltaX = Math.abs(x - touchStartPosRef.current.x);
            const deltaY = Math.abs(y - touchStartPosRef.current.y);
            if (deltaX > 10 || deltaY > 10) {
                hasMovedRef.current = true;
                if (longPressTimerRef.current) {
                    clearTimeout(longPressTimerRef.current);
                    longPressTimerRef.current = null;
                }
            }
        };

        const cleanup = () => {
            document.removeEventListener('touchmove', handleMove);
            document.removeEventListener('mousemove', handleMove);
        };

        document.addEventListener('touchmove', handleMove, { passive: true });
        document.addEventListener('mousemove', handleMove);
        setTimeout(cleanup, 350);
    };

    const handlePressEnd = (clientX: number, clientY: number, target: EventTarget | null) => {
        if (longPressTimerRef.current) {
            clearTimeout(longPressTimerRef.current);
            longPressTimerRef.current = null;
        }

        const now = Date.now();
        if (now - lastEndEventTimeRef.current < 150) return;
        lastEndEventTimeRef.current = now;

        if (hasMovedRef.current) {
            const distanceX = touchStart - clientX;
            const distanceY = touchStartPosRef.current.y - clientY;

            if (isFullscreen && Math.abs(distanceY) > 50) {
                setIsFullscreen(false);
                hasMovedRef.current = false;
                return;
            }

            if (Math.abs(distanceX) > 50) {
                handleSwipe(clientX);
            }
            hasMovedRef.current = false;
            return;
        }

        if (isLongPressRef.current) {
            if (videoRef.current) {
                videoRef.current.playbackRate = 1.0;
                setFeedbackIcon(null);
            }
            isLongPressRef.current = false;
            triggerControlsDisplay();
            return;
        }

        if (target && (target as HTMLElement).closest('input, button, .video-seekbar-container, .video-fullscreen-container')) return;

        setTouchEnd(clientX);
        const distance = touchStart - clientX;
        if (Math.abs(distance) > 50) {
            handleSwipe(clientX);
            return;
        }

        const DOUBLE_TAP_DELAY = 300;

        if (now - lastTapTimeRef.current < DOUBLE_TAP_DELAY) {
            // It's a double tap! Undo the play/pause state change triggered by the first tap
            if (videoRef.current) {
                if (videoRef.current.paused) {
                    videoRef.current.play().catch(() => { });
                } else {
                    videoRef.current.pause();
                }
            }

            if (tapTimeoutRef.current) {
                clearTimeout(tapTimeoutRef.current);
                tapTimeoutRef.current = null;
            }

            const containerWidth = containerRef.current?.offsetWidth || window.innerWidth;
            let relativeX = clientX;
            if (containerRef.current) {
                const rect = containerRef.current.getBoundingClientRect();
                relativeX = clientX - rect.left;
            }

            const isLeft = relativeX < containerWidth * 0.2;
            const isRight = relativeX > containerWidth * 0.8;

            if (videoRef.current) {
                if (isLeft) {
                    videoRef.current.currentTime = Math.max(0, videoRef.current.currentTime - 5);
                    setFeedbackIcon('rewind');
                } else if (isRight) {
                    videoRef.current.currentTime = Math.min(videoRef.current.duration, videoRef.current.currentTime + 5);
                    setFeedbackIcon('forward');
                }
            }
            lastTapTimeRef.current = 0;
            setTimeout(() => setFeedbackIcon(null), 600);
        } else {
            // It's a single tap! Toggle play/pause instantly
            lastTapTimeRef.current = now;
            triggerControlsDisplay();
            if (videoRef.current) {
                if (videoRef.current.paused) {
                    videoRef.current.play().catch(() => { });
                    setFeedbackIcon('play');
                } else {
                    videoRef.current.pause();
                    setFeedbackIcon('pause');
                }
            }
            setTimeout(() => setFeedbackIcon(null), 600);
        }
    };

    const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newTime = parseFloat(e.target.value);
        if (videoRef.current) {
            videoRef.current.currentTime = (newTime / 100) * videoRef.current.duration;
            setProgress(newTime);
        }
    };

    const handleTimeUpdate = () => {
        if (videoRef.current) {
            const current = videoRef.current.currentTime;
            const duration = videoRef.current.duration;
            if (duration) {
                setProgress((current / duration) * 100);
            }
        }
    };

    const handleSwipe = (endX: number) => {
        if (mediaItems.length <= 1) return;
        const swipeThreshold = 50;
        const distance = touchStart - endX;
        if (Math.abs(distance) > swipeThreshold) {
            if (distance > 0) nextMedia();
            else prevMedia();
        }
    };

    const handleNavigateAndClose = (userId: string) => {
        onNavigateToProfile(userId);
    };

    const handleNavigateHashtagAndClose = (tag: string) => {
        onNavigateToHashtag(tag);
    };

    const isBumpOfPrivateContent = post?.type === 'bump' &&
        post.originalPost &&
        !canViewPost(post.originalPost, currentUser, usersData, communities);

    const renderMediaContent = () => {
        if (isBumpOfPrivateContent) {
            return (
                <div className="w-full h-full flex items-center justify-center bg-gray-900">
                    <div className="bg-gray-800/50 rounded-2xl p-8 max-w-md mx-4 text-center">
                        <GoodieWhiteIcon className="w-16 h-16 mx-auto mb-4 opacity-50" />
                        <h2 className="text-2xl font-bold mb-2">Private Post</h2>
                        <p className="text-gray-400 mb-4">This content is private. Only followers of @{post?.originalPost?.author} can view it.</p>
                    </div>
                </div>
            );
        }

        const mediaContent = (() => {
            switch (postToShow.type) {
                case 'image':
                case 'video': {
                    if (mediaItems.length === 0) {
                        if (postToShow.isArchived) {
                            return (
                                <div className="w-full h-full flex flex-col items-center justify-center bg-gray-900 p-8 text-center">
                                    <GoodieWhiteIcon className="w-20 h-20 opacity-30 mb-6" />
                                    <h2 className="text-2xl font-bold mb-2">Media Removed</h2>
                                    <p className="text-gray-400 max-w-md">This media was uploaded prior to updated security protocols and has been permanently removed for your safety.</p>
                                </div>
                            );
                        }
                        return null;
                    }
                    const currentMedia = mediaItems[currentMediaIndex];
                    return (
                        <div
                            className={`media-swipe-container ${isFullscreen ? "fixed inset-0 z-[100] bg-black flex items-center justify-center select-none cursor-default" : (isMobilePortrait ? "w-full h-auto relative select-none" : "w-full h-full relative select-none")}`}
                            onTouchStart={(e) => handlePressStart(e.touches[0].clientX, e.touches[0].clientY)}
                            onTouchEnd={(e) => handlePressEnd(e.changedTouches[0].clientX, e.changedTouches[0].clientY, e.target)}
                            onMouseDown={(e) => handlePressStart(e.clientX, e.clientY)}
                            onMouseUp={(e) => handlePressEnd(e.clientX, e.clientY, e.target)}
                            onMouseLeave={(e) => isLongPressRef.current && handlePressEnd(e.clientX, e.clientY, null)}
                        >
                            {currentMedia.type === 'image' && (
                                <div
                                    className={`${isMobilePortrait ? "relative w-full h-auto" : "relative w-full h-full"} overflow-hidden rounded-2xl`}
                                    onTouchStart={handleZoomTouchStart}
                                    onTouchMove={handleZoomTouchMove}
                                    onTouchEnd={handleZoomTouchEnd}
                                >
                                    <img
                                        src={`${currentMedia.url}${currentMedia.url.includes('?') ? '&' : '?'}retry=${reloadKeys[currentMedia.url] || 0}`}
                                        alt={`Media ${currentMediaIndex + 1}`}
                                        className={isMobilePortrait ? "w-full h-auto object-contain rounded-2xl" : "w-full h-full object-contain rounded-2xl"}
                                        style={{
                                            transform: `translate(${zoomPosition.x}px, ${zoomPosition.y}px) scale(${zoomScale})`,
                                            transformOrigin: 'center center',
                                            transition: isDraggingZoomRef.current || initialDistanceRef.current !== null ? 'none' : 'transform 0.15s ease-out',
                                        }}
                                        onError={() => { console.error('[PostModal] Image failed to load:', currentMedia.url); if (!autoReloadMedia(currentMedia.url)) { setMediaErrors(prev => ({ ...prev, [currentMedia.url]: true })); } }}
                                    />
                                    {mediaErrors[currentMedia.url] && (
                                        <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center z-30 rounded-2xl">
                                            <span className="text-white text-xs font-bold mb-2 drop-shadow-md">Media failed to load</span>
                                            <button onClick={() => handleMediaReload(currentMedia.url)} className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-full text-white font-bold backdrop-blur-sm transition-all drop-shadow-md cursor-pointer">Reload</button>
                                        </div>
                                    )}
                                    {currentMedia.isAIGenerated && (
                                        <div className="absolute top-4 left-4 z-20 pointer-events-none opacity-80">
                                            <img src="/assets/goodie_ai_sticker.png" alt="AI Generated" className="w-12 h-14 drop-shadow-md" />
                                        </div>
                                    )}

                                </div>
                            )}
                            {currentMedia.type === 'video' && (
                                <div className={isMobilePortrait ? "relative w-full h-auto" : "relative w-full h-full"} ref={containerRef}>
                                    <video
                                        ref={videoRef}
                                        src={`${currentMedia.url}${currentMedia.url.includes('?') ? '&' : '?'}retry=${reloadKeys[currentMedia.url] || 0}#t=0.001`}
                                        className={isFullscreen ? "w-full h-full object-contain" : (isMobilePortrait ? "w-full h-auto object-contain rounded-2xl" : "w-full h-full object-contain rounded-2xl")}
                                        autoPlay={modalReady}
                                        loop
                                        muted={isMuted}
                                        playsInline
                                        preload="metadata"
                                        onTimeUpdate={handleTimeUpdate}
                                        onPlay={() => { setIsPlaying(true); triggerControlsDisplay(); }}
                                        onPause={() => { setIsPlaying(false); triggerControlsDisplay(); }}
                                        onVolumeChange={() => setIsMuted(videoRef.current?.muted ?? true)}
                                        onError={() => { console.error('[PostModal] Video failed to load:', currentMedia.url); if (!autoReloadMedia(currentMedia.url)) { setMediaErrors(prev => ({ ...prev, [currentMedia.url]: true })); } }}
                                    />
                                    {mediaErrors[currentMedia.url] && (
                                        <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center z-30 rounded-2xl">
                                            <span className="text-white text-xs font-bold mb-2 drop-shadow-md">Media failed to load</span>
                                            <button onClick={() => handleMediaReload(currentMedia.url)} className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-full text-white font-bold backdrop-blur-sm transition-all drop-shadow-md cursor-pointer">Reload</button>
                                        </div>
                                    )}
                                    {currentMedia.isLive && (
                                        <div className="absolute top-4 right-4 z-20 pointer-events-none opacity-50">
                                            <img src="/assets/goodieboy.png" alt="GoodLyfe Live" className="w-12 h-14" />
                                        </div>
                                    )}
                                    {currentMedia.isAIGenerated && (
                                        <div className="absolute top-4 left-4 z-20 pointer-events-none opacity-80">
                                            <img src="/assets/goodie_ai_sticker.png" alt="AI Generated" className="w-12 h-14 drop-shadow-md" />
                                        </div>
                                    )}
                                    {/* Fullscreen Toggle Button */}
                                    <div className={`absolute bottom-6 right-6 z-30 transition-opacity duration-300 ${showFullscreenControls ? 'opacity-100' : 'opacity-0'} video-fullscreen-container`}>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setIsFullscreen(!isFullscreen);
                                                triggerControlsDisplay();
                                            }}
                                            className=" text-white px-4 py-2  text-sm font-black uppercase transition-all shadow-lg active:scale-95"
                                        >
                                            {isFullscreen ? 'MINIMIZE' : 'FULLSCREEN'}
                                        </button>
                                    </div>
                                    {feedbackIcon && (
                                        <div className="absolute inset-0 flex items-center justify-center z-20 pointer-events-none animate-fade-out">
                                            <div className="bg-black/60 p-4 rounded-full">
                                                {feedbackIcon === 'play' && <PlayIcon className="w-12 h-12 text-white" />}
                                                {feedbackIcon === 'pause' && <PauseIcon className="w-12 h-12 text-white" />}
                                                {feedbackIcon === 'mute' && <VolumeOffIcon className="w-12 h-12 text-white" />}
                                                {feedbackIcon === 'unmute' && <VolumeHighIcon className="w-12 h-12 text-white" />}
                                                {feedbackIcon === 'forward' && <div className="text-white text-center"><span className="text-xs font-bold">+5s</span></div>}
                                                {feedbackIcon === 'rewind' && <div className="text-white text-center"><span className="text-xs font-bold">-5s</span></div>}
                                                {feedbackIcon === '2x' && <div className="text-white text-center"><span className="text-sm font-bold">2x Speed</span></div>}
                                            </div>
                                        </div>
                                    )}
                                    <div className={`absolute top-[80%] left-[10%] w-[80%] z-30 transition-opacity duration-300 ${showFullscreenControls ? 'opacity-100' : 'opacity-0'} video-seekbar-container`}>
                                        <input
                                            type="range"
                                            min="0"
                                            max="100"
                                            value={progress}
                                            onChange={handleSeek}
                                            onPointerDown={(e) => {
                                                e.stopPropagation();
                                                const rect = e.currentTarget.getBoundingClientRect();
                                                const clickX = e.clientX - rect.left;
                                                const width = rect.width;
                                                const newProgress = Math.max(0, Math.min(100, (clickX / width) * 100));
                                                if (videoRef.current && videoRef.current.duration) {
                                                    videoRef.current.currentTime = (newProgress / 100) * videoRef.current.duration;
                                                    setProgress(newProgress);
                                                }
                                            }}
                                            className="w-full h-[2px] bg-white/20 appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-0 [&::-webkit-slider-thumb]:h-0 transition-all"
                                            style={{
                                                backgroundImage: `linear-gradient(to right, #3b82f6 ${progress}%, rgba(255, 255, 255, 0.1) ${progress}%)`
                                            }}
                                        />
                                    </div>
                                </div>
                            )}
                            {mediaItems.length > 1 && (
                                <>
                                    <button onClick={(e) => { e.stopPropagation(); prevMedia(); }} className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 p-2 rounded-full text-white hover:bg-black/80 z-10"><ChevronLeftIcon className="w-6 h-6" /></button>
                                    <button onClick={(e) => { e.stopPropagation(); nextMedia(); }} className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 p-2 rounded-full text-white hover:bg-black/80 z-10"><ChevronRightIcon className="w-6 h-6" /></button>

                                    {/* Pagination Controls */}
                                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 z-10">
                                        {/* Carousel Dots */}
                                        <div className="flex justify-center gap-1.5 pointer-events-none">
                                            {mediaItems.map((_: any, idx: number) => (
                                                <div
                                                    key={idx}
                                                    className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${idx === currentMediaIndex ? 'bg-white scale-125 shadow-sm' : 'bg-white/40'}`}
                                                />
                                            ))}
                                        </div>
                                        {/* Fraction Indicator */}
                                        <div className="bg-black/50 text-white text-[10px] px-2 py-0.5 rounded-full font-medium">{currentMediaIndex + 1} / {mediaItems.length}</div>
                                    </div>
                                </>
                            )}
                            {post.linkRequestDetails?.isLinkRequest && (
                                <div className="absolute bottom-4 inset-x-4 z-40 flex justify-center pointer-events-auto">
                                    <div className="w-full max-w-md bg-black/60 backdrop-blur-xl border border-white/15 rounded-3xl p-2 shadow-2xl">
                                        <LinkRequestRSVPSection
                                            post={post}
                                            currentUser={currentUser}
                                            usersData={usersData}
                                            postThemeColor={currentUserData?.appThemeColor || '#3b82f6'}
                                            onRespondLinkRequest={onRespondLinkRequest}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                }
                case 'audio':
                case 'voice':
                    return (
                        <div className="w-full h-full relative flex items-center justify-center overflow-hidden">
                            {postToShow.content.coverImageUrl && (
                                <>
                                    <div className="absolute inset-0 z-0">
                                        <img src={postToShow.content.coverImageUrl} alt="" className="w-full h-full object-cover blur-2xl opacity-40 scale-110 rounded-2xl" />
                                    </div>
                                    <img src={postToShow.content.coverImageUrl} alt="Cover" className="relative z-10 w-full h-full object-contain shadow-lg rounded-2xl" />
                                </>
                            )}
                            <div className={`${postToShow.content.coverImageUrl ? 'absolute' : 'relative'} z-20 w-full max-w-md px-6`}>
                                <div
                                    className="rounded-2xl p-4 space-y-4 cursor-pointer"
                                    onClick={() => audioPlayerRef.current?.togglePlayPause()}
                                >
                                    {audioItems.map((audio, index) => (
                                        <div key={audio.url} className="space-y-3">
                                            <CustomAudioPlayer
                                                ref={index === 0 ? audioPlayerRef : null}
                                                src={audio.url}
                                                name={audio.name}
                                                autoplay={index === 0 && modalReady}
                                                post={postToShow}
                                            />
                                        </div>
                                    ))}
                                    {audioItems.length === 0 && (
                                        <div className="text-center py-4">
                                            <p className="text-gray-400 text-sm">Audio content not found</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                            {/* Relocated Track Badge - Bottom Left overlay */}
                            {audioItems[0]?.originalUrl && (
                                <div className="absolute bottom-4 left-4 z-30">
                                    <a
                                        href={audioItems[0].originalUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                        className="inline-flex items-center gap-1.5 bg-black/50 hover:bg-black/70 border border-white/10 rounded-full py-1 px-2.5 text-white transition-all active:scale-95 duration-300"
                                    >
                                        {postToShow.content.coverImageUrl ? (
                                            <img src={postToShow.content.coverImageUrl} className="w-4 h-4 rounded-md object-cover flex-shrink-0" alt="" />
                                        ) : (
                                            <span className="text-xs">🎵</span>
                                        )}
                                        <span className="text-[10px] font-bold text-white truncate max-w-[150px] uppercase tracking-wider">
                                            {audioItems[0].name || 'Open Track'}
                                        </span>
                                    </a>
                                </div>
                            )}
                        </div>
                    );
                case 'link': {
                    const { linkPreview } = postToShow.content;
                    if (!linkPreview) {
                        const linkUrl = postToShow.content.text?.match(/(https?:\/\/[^\s]+)/)?.[0] || '#';
                        return (
                            <div className="w-full h-full flex flex-col items-center justify-center p-8">
                                <a href={linkUrl} target="_blank" rel="noopener noreferrer" className="block w-full max-w-md bg-gray-900 rounded-lg overflow-hidden hover:opacity-80 transition-opacity">
                                    <div className="p-4">
                                        <p className="text-gray-300 mt-1">{postToShow.content.text}</p>
                                    </div>
                                </a>
                            </div>
                        );
                    }
                    const linkUrl = linkPreview.url || postToShow.content.text?.match(/(https?:\/\/[^\s]+)/)?.[0] || '#';
                    const domain = linkPreview.domain;

                    // Extract YouTube video ID
                    let ytVideoId = null;
                    if (domain === 'youtube.com' || domain === 'youtu.be') {
                        if (linkPreview.embedHtml) {
                            const match = linkPreview.embedHtml.match(/embed\/([^?"]+)/);
                            ytVideoId = match ? match[1] : null;
                        }
                        if (!ytVideoId) {
                            const urlStr = linkPreview?.url || linkUrl || '';
                            const shortsMatch = urlStr.match(/\/shorts\/([a-zA-Z0-9_-]+)/);
                            if (shortsMatch) ytVideoId = shortsMatch[1];
                            const watchMatch = urlStr.match(/(?:v=|\/embed\/|\/watch\?v=|\.be\/)([a-zA-Z0-9_-]{11})/);
                            if (watchMatch) ytVideoId = watchMatch[1];
                        }
                    }
                    const isYoutube = domain === 'youtube.com' || domain === 'youtu.be' || !!ytVideoId;

                    if (linkPreview?.embedHtml || (isYoutube && ytVideoId)) {
                        const isInsta = domain === 'instagram.com';
                        const isTiktok = domain === 'tiktok.com';
                        const isSoundCloud = domain === 'soundcloud.com';
                        const isAppleMusic = domain === 'music.apple.com';
                        const isSpotify = domain === 'spotify.com';
                        const isFacebook = domain === 'facebook.com' || domain === 'fb.watch';
                        const isShorts = isYoutube && (linkUrl.includes('/shorts/') || linkPreview?.url?.includes('/shorts/') || linkPreview?.isYouTubeShort);

                        let wrapperClass = "w-full h-full flex items-center justify-center [&>iframe]:w-full [&>iframe]:h-full";
                        if (isYoutube) {
                            if (isShorts) {
                                wrapperClass = "w-full max-w-[450px] aspect-[9/16] flex flex-col items-center justify-start overflow-hidden bg-black";
                            } else {
                                wrapperClass += " max-w-4xl aspect-video";
                            }
                        }
                        else if (isInsta) wrapperClass += " max-w-[540px]";
                        else if (isTiktok) wrapperClass = "w-full max-w-[450px] aspect-[9/16] flex items-center justify-center overflow-hidden";
                        else if (isSoundCloud || isAppleMusic || isSpotify) wrapperClass = "w-full max-w-[660px] h-[152px]";

                        return (
                            <div className="w-full h-full bg-black flex items-center justify-center relative">
                                {linkUrl !== '#' && !isFacebook && (
                                    <a href={linkUrl} target="_blank" rel="noopener noreferrer" className="absolute top-4 right-4 bg-gray-800/70 hover:bg-gray-700 text-white font-bold py-2 px-3 rounded-full transition-colors text-xs flex items-center gap-1.5 z-20">
                                        <ExternalLinkIcon className="w-4 h-4" />
                                        <span>{isYoutube ? "Watch on YouTube" : "Open Link"}</span>
                                    </a>
                                )}
                                <div className={wrapperClass} style={(isTiktok || isShorts) ? { aspectRatio: '9/16' } : undefined}>
                                    {(() => {
                                        let html = linkPreview.embedHtml || '';
                                        if (isYoutube && ytVideoId) {
                                            let videoIdToUse = ytVideoId;
                                            if (html) {
                                                const match = html.match(/src="([^"]+)"/);
                                                if (match) {
                                                    const src = match[1];
                                                    const videoIdMatch = src.match(/embed\/([^?"]+)/);
                                                    if (videoIdMatch) videoIdToUse = videoIdMatch[1];
                                                }
                                            }
                                            const embedSrc = `https://www.youtube.com/embed/${videoIdToUse}?controls=1&playsinline=1&rel=0`;
                                            return (
                                                <iframe
                                                    src={embedSrc}
                                                    style={{ width: '100%', height: '100%', border: 'none' }}
                                                    allow="autoplay; fullscreen; encrypted-media; picture-in-picture"
                                                    allowFullScreen
                                                    title="YouTube Video Player"
                                                />
                                            );
                                        } else if (domain === 'tiktok.com') {
                                            const videoId = resolvedTiktokVideoId;
                                            if (isTiktokLoading || !videoId) {
                                                return (
                                                    <div className="flex flex-col items-center justify-center w-full h-full min-h-[350px] text-white/60 font-semibold text-sm bg-black select-none">
                                                        <div className="relative flex items-center justify-center mb-6">
                                                            <div className="absolute w-12 h-12 rounded-full border border-t-white border-white/15 animate-spin duration-1000 ease-linear"></div>
                                                            <div className="absolute w-16 h-16 rounded-full border border-t-transparent border-dashed border-white/30 animate-spin duration-[2000ms] ease-out"></div>
                                                            <div 
                                                                className="w-4 h-4 rounded-full shadow-[0_0_15px_#fff]" 
                                                                style={{ backgroundColor: currentUserData?.appThemeColor || '#3b82f6', boxShadow: `0 0 20px ${currentUserData?.appThemeColor || '#3b82f6'}` }}
                                                            />
                                                        </div>
                                                        <span className="animate-pulse tracking-widest uppercase text-[10px] font-extrabold" style={{ color: currentUserData?.appThemeColor || '#3b82f6' }}>
                                                            Resolving Premium Video...
                                                        </span>
                                                    </div>
                                                );
                                            }
                                            return (
                                                <TiktokEmbed
                                                    videoId={videoId}
                                                    apiUrl={API_URL}
                                                />
                                            );
                                        } else if (isInsta) {
                                            const match = (linkPreview.url || linkUrl || '').match(/(?:p|reel)\/([a-zA-Z0-9_-]+)/);
                                            const mediaId = match ? match[1] : '';
                                            if (mediaId) {
                                                return (
                                                    <div style={{ width: '100%', height: '100%', overflow: 'hidden', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#000' }}>
                                                        <iframe
                                                            src={`https://www.instagram.com/p/${mediaId}/embed/`}
                                                            style={{ width: '100%', height: 'calc(100% + 110px)', marginTop: '-58px', marginBottom: '-52px', border: 'none', background: '#000' }}
                                                            allowFullScreen
                                                            allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                                                            scrolling="no"
                                                            title="Instagram Embed"
                                                        />
                                                    </div>
                                                );
                                            }
                                        } else if (domain === 'facebook.com' || domain === 'fb.watch') {
                                             return (
                                                 <FacebookEmbed url={linkUrl} preview={linkPreview} />
                                             );
                                         }
                                        return (
                                            <div
                                                className="w-full h-full"
                                                dangerouslySetInnerHTML={{ __html: html }}
                                            />
                                        );
                                    })()}
                                </div>
                            </div>
                        );
                    }

                    return (
                        <div className="w-full h-full flex flex-col items-center justify-center p-8">
                            <a href={linkUrl} target="_blank" rel="noopener noreferrer" className="block w-full max-w-md bg-gray-900 rounded-lg overflow-hidden hover:opacity-80 transition-opacity">
                                {linkPreview.image && <img src={linkPreview.image} className="w-full h-48 object-cover" onError={(e) => e.currentTarget.style.display = 'none'} />}
                                <div className="p-4">
                                    <h3 className="font-bold text-xl text-raised">{linkPreview.title}</h3>
                                    <p className="text-gray-300 mt-1">{linkPreview.description}</p>
                                    <p className="text-xs text-gray-500 mt-2 uppercase">{linkPreview.domain}</p>
                                </div>
                            </a>
                        </div>
                    );
                }
                default:
                    return (
                        <div className="w-full h-full flex items-center justify-center p-8">
                            <div className="text-3xl text-center text-raised whitespace-pre-wrap">
                                <FormattedText
                                    text={postToShow.content.text || ''}
                                    onNavigateToProfile={handleNavigateAndClose}
                                    onNavigateToHashtag={handleNavigateHashtagAndClose}
                                    textSize={postToShow.textSize || 'lg'}
                                    textColor={postTextColor}
                                    fontFamily={postFontFamily}
                                    stickerize={true}
                                />
                            </div>
                        </div>
                    );
            }
        })();
        return localCommunityName ? (() => {
            const community = communities?.find((c: any) => c._id === post!.communityId || c.id === post!.communityId);
            return (
                <div className="flex flex-col h-full w-full">
                    <div className="flex items-center justify-between p-2 pl-4">
                        <div className="flex items-center gap-2">
                            <button
                                onClick={(e) => { e.stopPropagation(); onNavigateToCommunity?.(post!.communityId!); }}
                                className="flex items-center gap-1.5 bg-black/50 rounded-full py-1.5 px-3 text-xs font-bold text-white hover:bg-black/70 transition-colors border border-white/10 flex-shrink-0 min-w-0"
                            >
                                {community?.avatar ? (
                                    <img src={community.avatar} alt={community.name} className="w-5 h-5 rounded-full object-cover flex-shrink-0" />
                                ) : (
                                    <UsersIcon className="w-4 h-4 text-blue-400 flex-shrink-0" />
                                )}
                                <span className="truncate">{community?.name || localCommunityName}</span>
                            </button>
                            {mediaItems[currentMediaIndex]?.isLive && (
                                <span className="bg-blue-500/20 px-2 py-0.5 rounded-full border border-blue-500/30 text-[9px] font-black uppercase tracking-[0.1em] text-blue-400 flex items-center gap-1.5">
                                    <img src="/assets/goodieboy.png" alt="" className="w-3.5 h-3.5" />
                                    Live
                                </span>
                            )}
                        </div>
                        <button
                            onClick={(e) => { e.stopPropagation(); onNavigateToCommunity?.(post!.communityId!, 'chat'); }}
                            className="p-2 text-blue-400 hover:text-blue-300 transition-colors"
                            title="Community Chat"
                        >
                            <MailIcon className="w-5 h-5" />
                        </button>
                    </div>
                    <div className="flex-1">
                        {mediaContent}
                    </div>
                </div>
            )
        })() : mediaContent;
    };

    const postAuthorRaw = isSimpleBump ? postToShow!.author : post!.author;
    const postAuthorKey = getCleanUserKey(postAuthorRaw) || postAuthorRaw;
    const postAuthorData = usersData[postAuthorKey] || {};

    const desktopView = (
        <div className={`flex w-full h-full ${isMobileLandscape ? 'max-w-full max-h-[100dvh]' : 'max-w-4xl max-h-[90vh]'}`} onClick={(e) => e.stopPropagation()}>
            <div className="flex-grow flex items-center justify-center" >
                {renderMediaContent()}
            </div>
            <div className="w-80 md:w-96 flex-shrink-0 flex flex-col p-4">
                <div className="flex items-start mb-4 pb-4 border-b border-gray-700/50">
                    <div className="flex flex-col flex-grow">
                        {mediaItems[currentMediaIndex]?.isLive && (
                            <div className="flex items-center gap-2 mb-2 px-1">
                                <div>
                                    <img src="/assets/goodieboy.png" alt="" className="w-8 h-8" />
                                </div>
                                <span className="text-[10px] font-black uppercase tracking-[0.1em] text-blue-400">GoodLyfe Live</span>
                            </div>
                        )}
                        {isSimpleBump && (() => {
                            const reposter = post!.author;
                            const reposterKey = getCleanUserKey(reposter) || reposter;
                            const reposterData = usersData[reposterKey];
                            const reposterTextColor = reposterData?.appTextColor || '#ffffff';
                            const reposterThemeColor = reposterData?.appThemeColor || '#000000';
                            return (
                                <div className="flex items-center gap-2 text-xs mb-2 font-bold px-1">
                                    <span className="sticker-outline" style={{ '--sticker-outline-color': reposterThemeColor } as React.CSSProperties}>
                                        <BumpIcon
                                            className="w-3.5 h-3.5"
                                            isBumped={true}
                                            fill="currentColor"
                                            stroke="currentColor"
                                            style={{
                                                color: reposterTextColor
                                            }}
                                        />
                                    </span>
                                    <span className="truncate" style={{ color: reposterTextColor, ...stickerTextStyle(0.35) }}>
                                        {(reposterData?.displayName || reposterKey)} bumped
                                    </span>
                                </div>
                            );
                        })()}
                        <button onClick={() => handleNavigateAndClose(postAuthorKey)} className="flex items-center text-left hover:opacity-80 transition-opacity">
                            <img src={postAuthorData?.pfp} alt={postAuthorKey} className="w-12 h-12 rounded-full mr-4 object-cover" />
                            <div>
                                <p className="font-bold" style={{ color: postTextColor, ...stickerTextStyle(0.35) }}>{postAuthorData?.displayName || postAuthorKey}</p>
                                <p className="text-sm text-gray-400 font-bold" style={stickerTextStyle(0.35)}>@{postAuthorKey.toLowerCase()}</p>
                            </div>
                        </button>
                    </div>
                    <div className="ml-auto flex flex-col items-end gap-2">
                        <div className="flex items-center gap-2">
                            {post.author === currentUser && (
                                <button onClick={() => onEdit(post)} className="p-1.5 text-gray-400 hover:text-white transition-all transform hover:scale-110 active:scale-95" aria-label="Edit post">
                                    <EditIcon className="w-5 h-5" />
                                </button>
                            )}
                            <div className="relative" ref={optionsRef}>
                                <button onClick={() => setIsOptionsOpen(!isOptionsOpen)} className="p-1.5 text-gray-400 hover:text-white transition-all transform hover:scale-110 active:scale-95"><MoreHorizontalIcon className="w-5 h-5" /></button>
                                {isOptionsOpen && (
                                    <div className="absolute top-full right-0 mt-2 w-48 bg-gray-900/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-gray-700/50 py-1.5 z-[60] animate-fade-in-fast overflow-hidden text-left">
                                        {post.author === currentUser ? (
                                            <>
                                                {!post.footprintDetails && onToggleVisibility && (
                                                    <button onClick={() => { onToggleVisibility(post.id); setIsOptionsOpen(false); }} className="w-full text-left px-4 py-2.5 hover:bg-white/10 transition-colors flex items-center gap-3">
                                                        {post.communityId ? (
                                                            post.visibility === 'public' ? <EyeOffIcon className="w-4 h-4 text-gray-400" /> : <EyeIcon className="w-4 h-4 text-gray-400" />
                                                        ) : (
                                                            post.visibility === 'public' ? <PrivateIcon className="w-4 h-4 text-gray-400" /> : <GlobeIcon className="w-4 h-4 text-gray-400" />
                                                        )}
                                                        <span className="text-sm font-medium text-white">
                                                            {post.communityId ? (
                                                                post.visibility === 'public' ? 'Hide from Profile' : 'Show on Profile'
                                                            ) : (
                                                                post.visibility === 'public' ? 'Make Private' : 'Make Public'
                                                            )}
                                                        </span>
                                                    </button>
                                                )}
                                                {onToggleCaption && (
                                                    <button onClick={() => { onToggleCaption(post.id); setIsOptionsOpen(false); }} className="w-full text-left px-4 py-2.5 hover:bg-white/10 transition-colors flex items-center gap-3">
                                                        {post.showCaption !== false ? <EyeOffIcon className="w-4 h-4 text-gray-400" /> : <EyeIcon className="w-4 h-4 text-gray-400" />}
                                                        <span className="text-sm font-medium text-white">{post.showCaption !== false ? 'Hide Caption' : 'Show Caption'}</span>
                                                    </button>
                                                )}
                                                {onArchive && (
                                                    <button
                                                        onClick={() => {
                                                            setIsOptionsOpen(false);
                                                            if (post.isArchived) {
                                                                onUnarchive?.(post.id);
                                                            } else {
                                                                setIsArchiveModalOpen(true);
                                                            }
                                                        }}
                                                        className={`w-full text-left px-4 py-2.5 hover:bg-yellow-500/10 text-yellow-400 transition-colors flex items-center gap-3 border-t border-gray-700/50`}
                                                    >
                                                        <BookmarkIcon className={`w-4 h-4 ${post.isArchived ? 'text-blue-400' : 'text-yellow-500'}`} />
                                                        <span className="text-sm font-medium">{post.isArchived ? 'Unarchive Post' : 'Archive Post'}</span>
                                                    </button>
                                                )}
                                                <button onClick={() => { setIsOptionsOpen(false); if (onDelete) onDelete(post.id, post); }} className="w-full text-left px-4 py-2.5 hover:bg-red-500/10 text-red-500 transition-colors flex items-center gap-3 border-t border-gray-700/50">
                                                    <TrashIcon className="w-4 h-4 text-red-500" />
                                                    <span className="text-sm font-medium">Delete Post</span>
                                                </button>

                                                <div className="mt-2 border-t border-gray-700/50 pt-3 px-4">
                                                    <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-2 text-left">Postcard Style</p>
                                                    <div className="flex flex-wrap gap-2">
                                                        {STORE_ITEMS.filter(item =>
                                                            item.category === 'postcards' &&
                                                            (item.id === 'classic_postcards' || currentUserData?.purchasedItems?.includes(item.id))
                                                        ).map((item) => {
                                                            const isSelected = (post.postcardStyle || 'classic_postcards') === item.id;
                                                            return (
                                                                <button
                                                                    key={item.id}
                                                                    onClick={() => {
                                                                        onUpdatePostcardStyle?.(post.id, item.id);
                                                                    }}
                                                                    className={`px-3 py-1.5 rounded-full text-[10px] font-bold transition-all border ${isSelected ? 'bg-blue-600 border-white text-white shadow-lg scale-105' : 'bg-gray-800 border-gray-700 text-gray-400 hover:text-white hover:border-gray-500'}`}
                                                                >
                                                                    {item.name.toUpperCase()}
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <button onClick={() => { onReportPost && onReportPost(post.id); setIsOptionsOpen(false); }} className="w-full text-left px-4 py-2.5 hover:bg-yellow-500/10 text-yellow-500 transition-colors flex items-center gap-3">
                                                    <FlagIcon className="w-4 h-4 text-yellow-500" />
                                                    <span className="text-sm font-medium">Report Content</span>
                                                </button>
                                                {onMuteUser && (
                                                    <button onClick={() => { onMuteUser(post.author); setIsOptionsOpen(false); }} className="w-full text-left px-4 py-2.5 hover:bg-white/10 transition-colors flex items-center gap-3">
                                                        <VolumeOffIcon className="w-4 h-4 text-gray-400" />
                                                        <span className="text-sm font-medium text-white">{usersData[currentUser]?.mutedUsers?.includes(post.author) ? 'Unmute' : 'Mute'} @{post.author}</span>
                                                    </button>
                                                )}
                                                {onBlockUser && (
                                                    <button onClick={() => { onBlockUser(post.author); setIsOptionsOpen(false); onClose(); }} className="w-full text-left px-4 py-2.5 hover:bg-red-500/10 text-red-400 transition-colors flex items-center gap-3 border-t border-gray-700/50">
                                                        <BrokenHeartIcon className="w-4 h-4 text-red-500" />
                                                        <span className="text-sm font-medium">{usersData[currentUser]?.blockedUsers?.includes(post.author) ? 'Unblock' : 'Block'} @{post.author}</span>
                                                    </button>
                                                )}
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                            <button onClick={(e) => { e.stopPropagation(); onClose(); }} style={{ touchAction: 'manipulation' }} className="p-1.5 text-gray-400 hover:text-white" aria-label="Close modal"><CloseIcon className="w-5 h-5" /></button>
                        </div>
                    </div>
                </div>

                <div className="flex-shrink-0 mb-3">
                    {post.createdAt && (
                        <div className="mb-1.5">
                            <div className="flex items-center gap-2 mb-1 px-1">
                                <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest opacity-70" style={stickerTextStyle(0.35)}>
                                    {formatRelativeTime(post.createdAt)}
                                </p>
                                {(post.type === 'audio' || post.type === 'voice') && (
                                    <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest opacity-70 flex items-center gap-1" style={stickerTextStyle(0.35)}>
                                        &bull; {post.playCount || 0} plays
                                    </p>
                                )}
                            </div>
                            {getModBadgesForPost(post, usersData, post.author).map((badge, idx) => {
                                const BadgeIcon = badge.icon;
                                return (
                                    <div
                                        key={idx}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            const ev = new CustomEvent(badge.onClickEvent, { detail: badge.onClickDetail });
                                            window.dispatchEvent(ev);
                                        }}
                                        className="inline-flex max-w-[80%] items-center gap-1.5 backdrop-blur-md px-2 py-1 rounded-full cursor-pointer hover:brightness-110 transition-colors z-20 shadow-sm border border-white/10 ml-1"
                                        style={{ backgroundColor: hexToRgba(badge.themeColor, 0.5) || 'rgba(0, 0, 0, 0.5)' }}
                                        title={badge.title}
                                    >
                                        {typeof BadgeIcon === 'string' ? (
                                            <img src={BadgeIcon} className="w-4 h-4 flex-shrink-0 rounded-full object-cover" alt={badge.title} />
                                        ) : BadgeIcon ? (
                                            React.createElement(BadgeIcon, { className: "w-4 h-4 flex-shrink-0" })
                                        ) : badge.fallbackIconName === 'footprint' ? (
                                            <img src="/assets/footprint_logo.png" className="w-4 h-4 flex-shrink-0 rounded-full" alt="Footprint" />
                                        ) : (
                                            <BellIcon className="w-4 h-4 flex-shrink-0" />
                                        )}
                                        <span className="text-[10px] font-medium truncate text-white" style={stickerTextStyle(0.18)}>{badge.title}</span>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                    {post.content.text && (
                        <div className="mb-3 flex items-start gap-2 justify-between w-full">
                            {post.type !== 'text' ? (
                                <div className="whitespace-pre-wrap flex-grow">
                                    <FormattedText text={post.content.text || ''} onNavigateToProfile={handleNavigateAndClose} onNavigateToHashtag={handleNavigateHashtagAndClose} textSize={post.textSize || 'med'} textColor={postTextColor} fontFamily={postFontFamily} stickerize={true} />
                                </div>
                            ) : (
                                <div className="flex-grow"></div>
                            )}
                            <button
                                onClick={() => handleCopyCaption(post.content.text || '')}
                                className="p-1 rounded hover:bg-white/10 text-gray-400 hover:text-white transition-all flex-shrink-0 active:scale-90 mt-1 cursor-pointer"
                                title={post.type === 'text' ? "Copy Text" : "Copy Caption"}
                            >
                                {copiedText ? (
                                    <span className="text-[10px] text-green-400 font-bold uppercase tracking-wider animate-pulse">Copied!</span>
                                ) : (
                                    <CopyIcon className="w-3.5 h-3.5" />
                                )}
                            </button>
                        </div>
                    )}
                    {post.type === 'bump' && (
                        <button onClick={() => onPostClick && onPostClick(postToShow)} className="flex w-full items-center mb-3 p-3 bg-transparent border border-white/10 rounded-lg text-left hover:opacity-80 transition-opacity">
                            <img src={usersData[postToShow.author]?.pfp} alt={postToShow.author} className="w-10 h-10 rounded-full mr-2 object-cover" />
                            <div className="whitespace-pre-wrap"><FormattedText text={postToShow.type !== 'text' ? postToShow.content.text || 'Original Post' : 'Original Post'} onNavigateToProfile={handleNavigateAndClose} onNavigateToHashtag={handleNavigateHashtagAndClose} textSize={postToShow.textSize || 'med'} textColor={postTextColor} fontFamily={postFontFamily} stickerize={true} /></div>
                        </button>
                    )}
                    <h3 className="font-bold mb-1 text-sm" style={{ color: postTextColor, ...stickerTextStyle(0.35) }}>Comments ({post.comments.length})</h3>
                </div>

                <div className="flex-grow overflow-y-auto overflow-x-hidden allow-scroll pr-1">
                    <div className="space-y-4">
                        {(() => {
                            const renderComment = (comment: any, depth = 0) => {
                                const replies = post.comments.filter(c => c.parentId === comment.id);
                                const cleanAuthor = comment.author.startsWith('@') ? comment.author.slice(1) : comment.author;
                                const commenterKey = Object.keys(usersData).find(key => key.toLowerCase() === cleanAuthor.toLowerCase()) || cleanAuthor;
                                const commenterData = usersData[commenterKey] || {};
                                const commenterThemeColor = enforceThemeColor ? (currentUserData?.appThemeColor || '#161b22') : (commenterData.appThemeColor || '#161b22');
                                const commenterTextColor = currentUserData?.enforceTextColor ? (currentUserData.appTextColor || '#ffffff') : (commenterData.appTextColor || '#ffffff');
                                const commenterOpacity = (enforceThemeOpacity ? (currentUserData?.postBlurOpacity ?? 50) : (commenterData.postBlurOpacity ?? 50)) / 100;
                                return (
                                    <React.Fragment key={comment.id}>
                                        <div className="relative">
                                            <ChatBubble
                                                text={comment.text}
                                                author={cleanAuthor}
                                                pfp={commenterData.pfp}
                                                themeColor={commenterThemeColor}
                                                textColor={commenterTextColor}
                                                opacity={commenterOpacity}
                                                reactions={comment.reactions}
                                                currentUser={currentUser || ''}
                                                onReactionClick={(emoji) => onCommentReaction?.(post.id, comment.id, emoji)}
                                                onNavigateToProfile={handleNavigateAndClose}
                                                onNavigateToHashtag={handleNavigateHashtagAndClose}
                                                onTouchStart={(e) => handleCommentTouchStart(e, comment)}
                                                onTouchMove={handleCommentTouchMove}
                                                onTouchEnd={handleCommentTouchEnd}
                                                onMouseDown={(e) => handleCommentTouchStart(e, comment)}
                                                onMouseMove={handleCommentTouchMove}
                                                onMouseUp={handleCommentTouchEnd}
                                                onMouseLeave={handleCommentTouchEnd}
                                                onContextMenu={(e) => handleCommentContextMenu(e, comment)}
                                                depth={depth}
                                                media={comment.media}
                                            />
                                            {commentToDelete === comment.id && (
                                                <div className="absolute inset-0 bg-gray-900/95 flex items-center justify-center gap-4 z-10 animate-fade-in backdrop-blur-md rounded-2xl border border-red-500/20">
                                                    <span className="text-xs font-bold text-gray-300">Delete this comment?</span>
                                                    <button onClick={() => { onDeleteComment && onDeleteComment(post.id, comment.id); setCommentToDelete(null); }} className="bg-red-600 hover:bg-red-700 text-white text-[10px] font-bold py-1.5 px-5 rounded-full transition-all active:scale-95 shadow-lg shadow-red-900/20">Confirm</button>
                                                    <button onClick={() => setCommentToDelete(null)} className="bg-gray-700 hover:bg-gray-600 text-white text-[10px] font-bold py-1.5 px-5 rounded-full transition-all active:scale-95">Cancel</button>
                                                </div>
                                            )}
                                        </div>
                                        {replies.length > 0 && (
                                            <div className="replies-container w-full overflow-x-hidden">
                                                {replies.map(reply => renderComment(reply, depth + 1))}
                                            </div>
                                        )}
                                    </React.Fragment>
                                );
                            };
                            const rootComments = post.comments.filter(c => !c.parentId);
                            return rootComments.length > 0 ? rootComments.map(c => renderComment(c)) : (
                                <div className="py-12 flex flex-col items-center justify-center opacity-40">
                                    <CommentIcon className="w-12 h-12 mb-3" />
                                    <p className="text-sm font-medium">No comments yet. Be the first!</p>
                                </div>
                            );
                        })()}
                    </div>
                </div>

                <div className="mt-auto pt-4 border-t border-gray-700">
                    {post.linkRequestDetails?.isLinkRequest ? (
                        mediaItems.length === 0 ? (
                            <div className="mb-4">
                                <LinkRequestRSVPSection
                                    post={post}
                                    currentUser={currentUser}
                                    usersData={usersData}
                                    postThemeColor={currentUserData?.appThemeColor || '#3b82f6'}
                                    onRespondLinkRequest={onRespondLinkRequest}
                                />
                            </div>
                        ) : null
                    ) : (
                        <div className="flex items-center space-x-4 mb-4" style={{ color: postTextColor }}>
                            {post.footprintDetails ? (
                                <button className="flex items-center gap-2 hover:opacity-80 transition-transform hover:scale-105 active:scale-95" onClick={() => onVouch && onVouch(post.id)}>
                                    <CheckmarkIcon isVouched={post.vouches?.some(v => v.userId === currentUser)} className={`w-6 h-6 ${post.vouches?.some(v => v.userId === currentUser) ? 'text-violet-500' : ''}`} style={!post.vouches?.some(v => v.userId === currentUser) ? { color: postTextColor } : {}} />
                                    <span className="text-sm">{post.vouches?.length || 0}</span>
                                </button>
                            ) : (
                                <button className="flex items-center gap-2 hover:opacity-80 transition-transform hover:scale-105 active:scale-95" onClick={() => onLike(post.id)}>
                                    <LikeIcon isLiked={post.isLiked} className={`w-6 h-6 ${post.isLiked ? 'text-red-500' : ''} ${animatingLikes.has(post.id) ? 'animate-heart-burst' : ''}`} style={!post.isLiked ? { color: postTextColor } : {}} />
                                    <span className="text-sm">{post.likes}</span>
                                </button>
                            )}
                            <button className="flex items-center gap-2 hover:opacity-80 transition-transform hover:scale-105 active:scale-95" onClick={() => onBump(postToShow)}>
                                <BumpIcon isBumped={postToShow.isBumped} className={`w-6 h-6 ${postToShow.isBumped ? 'text-emerald-500' : ''} ${animatingBumps.has(postToShow.id) ? 'animate-bump-spin' : ''}`} style={!postToShow.isBumped ? { color: postTextColor } : {}} />
                                <span className="text-sm">{postToShow.bumps}</span>
                            </button>
                            <button className="flex items-center gap-2 hover:opacity-80 transition-transform hover:scale-105 active:scale-95" onClick={() => onShare && onShare(post)}>
                                <SendIcon className={`w-6 h-6`} style={{ color: postTextColor }} />
                                <span className="text-sm">{post.shares || 0}</span>
                            </button>
                            <button className="flex items-center gap-2 hover:opacity-80 transition-transform hover:scale-105 active:scale-95" onClick={() => onBookmark(post.id)}>
                                <BookmarkIcon isBookmarked={post.isBookmarked} className={`w-6 h-6 ${post.isBookmarked ? 'text-blue-500' : ''} ${animatingBookmarks.has(post.id) ? 'animate-bookmark-pop' : ''}`} style={!post.isBookmarked ? { color: postTextColor } : {}} />
                                <span className="text-sm">{currentUser === post.author ? post.bookmarks : ''}</span>
                            </button>
                        </div>
                    )}
                    {replyTo && (
                        <div className="flex items-center justify-between bg-blue-500/10 border-l-2 border-blue-500 px-3 py-1.5 mb-2 rounded-r-lg animate-fade-in">
                            <p className="text-[10px] text-blue-400 font-bold uppercase tracking-wider">
                                Replying to <span className="text-white">@{(replyTo as any).author}</span>
                            </p>
                            <button onClick={() => setReplyTo(null)} className="text-gray-400 hover:text-white p-1">
                                <CloseIcon className="w-3 h-3" />
                            </button>
                        </div>
                    )}
                    {editingComment && (
                        <div className="flex items-center justify-between bg-orange-500/10 border-l-2 border-orange-500 px-3 py-1.5 mb-2 rounded-r-lg animate-fade-in">
                            <p className="text-[10px] text-orange-400 font-bold uppercase tracking-wider">
                                Editing comment
                            </p>
                            <button onClick={() => { setEditingComment(null); setNewComment(""); }} className="text-gray-400 hover:text-white p-1">
                                <CloseIcon className="w-3 h-3" />
                            </button>
                        </div>
                    )}
                    {attachments.length > 0 && (
                        <div className="flex flex-wrap gap-2 pb-2">
                            {attachments.map((at, idx) => (
                                <div key={idx} className="relative group flex-shrink-0">
                                    <div className={`${(at.type === 'audio' || at.type === 'voice') ? 'w-full' : 'w-12'} h-12 rounded-xl overflow-hidden bg-black/20`}>
                                        {at.type === 'image' && <img src={at.url} className="w-full h-full object-cover" alt="" />}
                                        {at.type === 'video' && <video src={`${at.url}#t=0.001`} className="w-full h-full object-cover" muted playsInline />}
                                        {(at.type === 'audio' || at.type === 'voice') && (
                                            <div className="p-1 min-w-[120px]">
                                                <CustomAudioPlayer src={at.url} />
                                            </div>
                                        )}
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => removeAttachment(idx)}
                                        className="absolute -top-1.5 -right-1.5 bg-red-600 text-white rounded-full p-0.5 shadow-lg flex items-center justify-center hover:bg-red-500 transition-colors"
                                    >
                                        <CloseIcon className="w-3 h-3" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                    {showMessageRecorder && (
                        <div className="bg-gray-900/80 backdrop-blur-sm rounded-xl px-4 py-2 border border-blue-500/30 min-h-[48px] flex items-center mb-1">
                            <AudioRecorder
                                ref={audioRecorderRef}
                                onRecordingComplete={handleVoiceMessageComplete}
                                onReset={handleVoiceMessageReset}
                                autoStart={true}
                                showControls={false}
                                stream={audioStream}
                            />
                        </div>
                    )}
                    <form onSubmit={handleCommentSubmit} className="flex items-start gap-3">
                        <img src={usersData[currentUser || '']?.pfp} alt="Your profile" className="w-10 h-10 rounded-full flex-shrink-0 object-cover" />
                        <div className="flex-grow relative flex items-center bg-gray-800 rounded-2xl border border-white/5 focus-within:ring-2 focus-within:ring-blue-500/50 transition-all">
                            <div className="flex-none relative pl-2">
                                <button
                                    type="button"
                                    onClick={() => setShowInputMenu(!showInputMenu)}
                                    className={`p-2 rounded-xl transition-all ${showInputMenu ? 'bg-white/10 text-white' : 'text-gray-400 hover:bg-white/5 hover:text-white'}`}
                                    title="More options"
                                >
                                    <PlusIcon className={`w-5 h-5 transition-transform duration-200 ${showInputMenu ? 'rotate-45' : ''}`} />
                                </button>
                                {showInputMenu && (
                                    <>
                                        <div className="fixed inset-0 z-40" onClick={() => setShowInputMenu(false)} />
                                        <div className="absolute bottom-full left-0 mb-2 bg-[#1b1b1f] border border-white/10 rounded-full shadow-2xl overflow-hidden flex flex-row items-center p-1 animate-fade-in z-50">
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setShowInputMenu(false);
                                                    const isAndroid = Capacitor.getPlatform() === 'android' || (typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent));
                                                    if (isAndroid) {
                                                        setCameraMode('image');
                                                        setShowCamera(true);
                                                    } else {
                                                        setCameraMode('both');
                                                        setShowCamera(true);
                                                    }
                                                }}
                                                className="p-2.5 hover:bg-white/5 rounded-full transition-colors text-white"
                                                title="Camera"
                                            >
                                                <CameraIcon className="w-5 h-5 text-blue-400" />
                                            </button>
                                            {(() => {
                                                const isAndroid = Capacitor.getPlatform() === 'android' || (typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent));
                                                if (isAndroid) {
                                                    return (
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                setShowInputMenu(false);
                                                                setCameraMode('video');
                                                                setShowCamera(true);
                                                            }}
                                                            className="p-2.5 hover:bg-white/5 rounded-full transition-colors text-white"
                                                            title="Video"
                                                        >
                                                            <VideoIcon className="w-5 h-5 text-purple-400" />
                                                        </button>
                                                    );
                                                }
                                                return null;
                                            })()}
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setShowInputMenu(false);
                                                    handleGalleryClick();
                                                }}
                                                className="p-2.5 hover:bg-white/5 rounded-full transition-colors text-white"
                                                title="Gallery"
                                            >
                                                <PaperclipIcon className="w-5 h-5 text-green-400" />
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setShowInputMenu(false);
                                                    setShowTenorModal(true);
                                                }}
                                                className="p-2.5 hover:bg-white/5 rounded-full transition-colors text-white"
                                                title="GIF"
                                            >
                                                <span className="text-[10px] font-black tracking-widest px-1.5 py-1 rounded bg-pink-500/20 text-pink-400 border border-pink-500/30 leading-none flex items-center justify-center h-5">GIF</span>
                                            </button>
                                            <button
                                                type="button"
                                                onClick={async () => {
                                                    setShowInputMenu(false);
                                                    if (!showMessageRecorder) {
                                                        try {
                                                            await requestMicrophonePermission();
                                                            const stream = await navigator.mediaDevices.getUserMedia({
                                                                audio: {
                                                                    echoCancellation: false,
                                                                    noiseSuppression: true,
                                                                    autoGainControl: false,
                                                                    sampleRate: 48000,
                                                                    sampleSize: 16,
                                                                    channelCount: 1
                                                                }
                                                            });
                                                            setAudioStream(stream);
                                                            setShowMessageRecorder(true);
                                                        } catch (err) {
                                                            console.error("Microphone access failed:", err);
                                                        }
                                                    } else {
                                                        audioRecorderRef.current?.stopRecording();
                                                        setShowMessageRecorder(false);
                                                    }
                                                }}
                                                className={`p-2.5 hover:bg-white/5 rounded-full transition-colors ${showMessageRecorder ? 'text-red-400' : 'text-white'}`}
                                                title="Voice"
                                            >
                                                <MicIcon className={`w-5 h-5 ${showMessageRecorder ? 'text-red-400 animate-pulse' : 'text-red-400'}`} />
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setShowInputMenu(false);
                                                    setShowEmojiPickerFor('comment-input');
                                                }}
                                                className="p-2.5 hover:bg-white/5 rounded-full transition-colors text-white"
                                                title="Emoji"
                                            >
                                                <EmojiIcon className="w-5 h-5 text-yellow-400" />
                                            </button>
                                        </div>
                                    </>
                                )}
                            </div>
                            <textarea
                                value={newComment}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    setNewComment(val);
                                    updateSearch(val, e.target.selectionStart);
                                }}
                                onKeyDown={handleCommentKeyDown}
                                onPaste={handlePaste}
                                placeholder={editingComment ? "Edit your comment..." : (replyTo ? `Reply to @${(replyTo as any).author}...` : "What you got to say?")}
                                className="flex-1 bg-transparent py-2.5 pl-2 pr-12 focus:outline-none resize-none text-sm transition-all custom-comment-textarea"
                                rows={1}
                                style={{ minHeight: '44px', maxHeight: '120px', overflow: 'auto' }}
                                disabled={isUploadingMedia}
                            />
                            {mentionQuery !== null && (
                                <MentionDropdown
                                    query={mentionQuery}
                                    following={usersData[currentUser || '']?.following || []}
                                    usersData={usersData}
                                    onSelect={(username) => {
                                        const { newText, newCursorPos } = completeMention(newComment, username, newComment.length);
                                        setNewComment(newText);
                                        setTimeout(() => {
                                            const el = document.querySelector('.custom-comment-textarea') as HTMLTextAreaElement;
                                            if (el) {
                                                el.focus();
                                                el.setSelectionRange(newCursorPos, newCursorPos);
                                            }
                                        }, 10);
                                    }}
                                    position="above"
                                />
                            )}
                            <button type="submit" className="absolute right-1.5 p-2 text-white rounded-full hover:bg-blue-500 transition-all disabled:opacity-50 bg-gray-700 shadow-sm active:scale-90" disabled={(!newComment.trim() && attachments.length === 0) || isUploadingMedia}>
                                {isUploadingMedia ? <span className="animate-spin inline-block w-4 h-4 border-2 border-t-transparent border-current rounded-full" /> : <CommentIcon className="w-4 h-4" />}
                            </button>
                        </div>
                        <input type="file" id="comment-file-desktop" ref={commentFileInputRef} multiple hidden onChange={handleMediaUpload} accept="*/*" />
                    </form>
                </div>
            </div>
        </div>
    );

    const mobileView = (
        <div className="flex flex-col w-full h-full max-h-[100dvh]" style={{ backgroundColor: modalBg }} onClick={(e) => e.stopPropagation()}>
            <div className="flex flex-col p-4 flex-shrink-0" style={{ paddingTop: 'calc(2.5rem + var(--safe-area-top, 0px))' }}>
                {isSimpleBump && (() => {
                    const reposter = post!.author;
                    const reposterKey = getCleanUserKey(reposter) || reposter;
                    const reposterData = usersData[reposterKey];
                    const reposterTextColor = reposterData?.appTextColor || '#ffffff';
                    const reposterThemeColor = reposterData?.appThemeColor || '#000000';
                    return (
                        <div className="flex items-center gap-2 text-[10px] mb-2 font-bold px-1">
                            <span className="sticker-outline" style={{ '--sticker-outline-color': reposterThemeColor } as React.CSSProperties}>
                                <BumpIcon
                                    className="w-3.5 h-3.5"
                                    isBumped={true}
                                    fill="currentColor"
                                    stroke="currentColor"
                                    style={{
                                        color: reposterTextColor
                                    }}
                                />
                            </span>
                            <span className="truncate" style={{ color: reposterTextColor, ...stickerTextStyle(0.35) }}>
                                {(reposterData?.displayName || reposterKey)} bumped
                            </span>
                        </div>
                    );
                })()}
                <div className="flex items-center justify-between">
                    <button onClick={() => handleNavigateAndClose(postAuthorKey)} className="flex items-center text-left hover:opacity-80 transition-opacity flex-grow">
                        <img src={postAuthorData?.pfp} alt={postAuthorKey} className="w-10 h-10 rounded-full mr-3 object-cover" />
                        <div>
                            <p className="font-bold text-sm" style={{ color: postTextColor, ...stickerTextStyle(0.35) }}>{postAuthorData?.displayName || postAuthorKey}</p>
                            <p className="text-xs text-gray-400 font-bold" style={stickerTextStyle(0.35)}>@{postAuthorKey.toLowerCase()}</p>
                        </div>
                    </button>
                    <div className="flex items-center gap-2">
                        {post.author === currentUser && (
                            <button onClick={() => onEdit(post)} className="p-2 text-gray-400 hover:text-white transition-all transform hover:scale-110 active:scale-95" aria-label="Edit post">
                                <EditIcon className="w-5 h-5" />
                            </button>
                        )}
                        <div className="relative" ref={optionsRef}>
                            <button onClick={() => setIsOptionsOpen(!isOptionsOpen)} className="p-2 text-gray-400 hover:text-white transition-all transform hover:scale-110 active:scale-95"><MoreHorizontalIcon className="w-5 h-5" /></button>
                            {isOptionsOpen && (
                                <div className="absolute top-full right-0 mt-2 w-48 bg-gray-900/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-gray-700/50 py-1.5 z-[60] animate-fade-in-fast overflow-hidden text-left">
                                    {post.author === currentUser ? (
                                        <>
                                            {!post.footprintDetails && onToggleVisibility && (
                                                <button onClick={() => { onToggleVisibility(post.id); setIsOptionsOpen(false); }} className="w-full text-left px-4 py-3 hover:bg-white/10 transition-colors flex items-center gap-3">
                                                    {post.communityId ? (
                                                        post.visibility === 'public' ? <EyeOffIcon className="w-4 h-4 text-gray-400" /> : <EyeIcon className="w-4 h-4 text-gray-400" />
                                                    ) : (
                                                        post.visibility === 'public' ? <PrivateIcon className="w-4 h-4 text-gray-400" /> : <GlobeIcon className="w-4 h-4 text-gray-400" />
                                                    )}
                                                    <span className="text-sm font-medium text-white">
                                                        {post.communityId ? (
                                                            post.visibility === 'public' ? 'Hide from Profile' : 'Show on Profile'
                                                        ) : (
                                                            post.visibility === 'public' ? 'Make Private' : 'Make Public'
                                                        )}
                                                    </span>
                                                </button>
                                            )}
                                            {onToggleCaption && (
                                                <button onClick={() => { onToggleCaption(post.id); setIsOptionsOpen(false); }} className="w-full text-left px-4 py-3 hover:bg-white/10 transition-colors flex items-center gap-3">
                                                    {post.showCaption !== false ? <EyeOffIcon className="w-4 h-4 text-gray-400" /> : <EyeIcon className="w-4 h-4 text-gray-400" />}
                                                    <span className="text-sm font-medium text-white">{post.showCaption !== false ? 'Hide Caption' : 'Show Caption'}</span>
                                                </button>
                                            )}
                                            {onArchive && (
                                                <button onClick={() => { setIsOptionsOpen(false); setIsArchiveModalOpen(true); }} className="w-full text-left px-4 py-3 hover:bg-yellow-500/10 text-yellow-400 transition-colors flex items-center gap-3 border-t border-gray-700/50">
                                                    <BookmarkIcon className="w-4 h-4 text-yellow-500" />
                                                    <span className="text-sm font-medium">Archive Post</span>
                                                </button>
                                            )}
                                            <button onClick={() => { setIsOptionsOpen(false); if (onDelete) onDelete(post.id, post); }} className="w-full text-left px-4 py-3 hover:bg-red-500/10 text-red-500 transition-colors flex items-center gap-3 border-t border-gray-700/50">
                                                <TrashIcon className="w-4 h-4 text-red-500" />
                                                <span className="text-sm font-medium">Delete Post</span>
                                            </button>
                                        </>
                                    ) : (
                                        <>
                                            <button onClick={() => { onReportPost && onReportPost(post.id); setIsOptionsOpen(false); }} className="w-full text-left px-4 py-3 hover:bg-yellow-500/10 text-yellow-500 transition-colors flex items-center gap-3">
                                                <FlagIcon className="w-4 h-4 text-yellow-500" />
                                                <span className="text-sm font-medium">Report Content</span>
                                            </button>
                                            {onMuteUser && (
                                                <button onClick={() => { onMuteUser(post.author); setIsOptionsOpen(false); }} className="w-full text-left px-4 py-3 hover:bg-white/10 transition-colors flex items-center gap-3">
                                                    <VolumeOffIcon className="w-4 h-4 text-gray-400" />
                                                    <span className="text-sm font-medium text-white">{usersData[currentUser]?.mutedUsers?.includes(post.author) ? 'Unmute' : 'Mute'} @{post.author}</span>
                                                </button>
                                            )}
                                            {onBlockUser && (
                                                <button onClick={() => { onBlockUser(post.author); setIsOptionsOpen(false); onClose(); }} className="w-full text-left px-4 py-3 hover:bg-red-500/10 text-red-400 transition-colors flex items-center gap-3 border-t border-gray-700/50">
                                                    <BrokenHeartIcon className="w-4 h-4 text-red-500" />
                                                    <span className="text-sm font-medium">{usersData[currentUser]?.blockedUsers?.includes(post.author) ? 'Unblock' : 'Block'} @{post.author}</span>
                                                </button>
                                            )}
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                        <button onClick={(e) => { e.stopPropagation(); onClose(); }} style={{ touchAction: 'manipulation' }} className="p-2 text-gray-400 hover:text-white" aria-label="Close modal"><CloseIcon className="w-5 h-5" /></button>
                    </div>
                </div>
            </div>

            <div className="flex-grow overflow-y-auto overflow-x-hidden flex flex-col items-center justify-start px-2 py-4 space-y-4 allow-scroll">
                <div className="w-full max-w-md flex items-center justify-center">
                    {renderMediaContent()}
                </div>
                {post.createdAt && (
                    <div className="w-full mb-2">
                        <div className="flex items-center gap-2 w-full">
                            <p className="text-[12px] font-bold text-gray-500 uppercase tracking-widest opacity-70" style={stickerTextStyle(0.35)}>
                                {formatRelativeTime(post.createdAt)}
                            </p>
                            {(post.type === 'audio' || post.type === 'voice') && (
                                <p className="text-[12px] font-bold text-gray-500 uppercase tracking-widest opacity-70 flex items-center gap-1" style={stickerTextStyle(0.35)}>
                                    &bull; {post.playCount || 0} plays
                                </p>
                            )}
                        </div>
                        {mediaItems[currentMediaIndex]?.isLive && (
                            <div className="flex items-center gap-1.5 mt-1">
                                <div>
                                    <img src="/assets/goodieboy.png" alt="" className="w-8 h-9" />
                                </div>
                                <span className="text-[10px] font-black uppercase tracking-[0.1em] text-raised text-red-400">GoodLyfe Live</span>
                            </div>
                        )}
                        {getModBadgesForPost(post, usersData, post.author).map((badge, idx) => {
                            const BadgeIcon = badge.icon;
                            return (
                                <div
                                    key={idx}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        const ev = new CustomEvent(badge.onClickEvent, { detail: badge.onClickDetail });
                                        window.dispatchEvent(ev);
                                    }}
                                    className="inline-flex max-w-[80%] items-center gap-1.5 backdrop-blur-md px-2 py-1 rounded-full cursor-pointer hover:brightness-110 transition-colors z-20 shadow-sm border border-white/10 mt-1"
                                    style={{ backgroundColor: hexToRgba(badge.themeColor, 0.5) || 'rgba(0, 0, 0, 0.5)' }}
                                    title={badge.title}
                                >
                                    {typeof BadgeIcon === 'string' ? (
                                        <img src={BadgeIcon} className="w-4 h-4 flex-shrink-0 rounded-full object-cover" alt={badge.title} />
                                    ) : BadgeIcon ? (
                                        React.createElement(BadgeIcon, { className: "w-4 h-4 flex-shrink-0" })
                                    ) : badge.fallbackIconName === 'footprint' ? (
                                        <img src="/assets/footprint_logo.png" className="w-4 h-4 flex-shrink-0 rounded-full" alt="Footprint" />
                                    ) : (
                                        <BellIcon className="w-4 h-4 flex-shrink-0" />
                                    )}
                                    <span className="text-[10px] font-medium truncate text-white" style={stickerTextStyle(0.18)}>{badge.title}</span>
                                </div>
                            );
                        })}
                    </div>
                )}

                {post.content.text && (
                    <div className="mb-4 flex items-start gap-2 justify-between w-full">
                        {post.type !== 'text' ? (
                            <div className="whitespace-pre-wrap flex-grow">
                                <FormattedText text={post.content.text || ''} onNavigateToProfile={handleNavigateAndClose} onNavigateToHashtag={handleNavigateHashtagAndClose} textSize={post.textSize || 'med'} textColor={postTextColor} fontFamily={postFontFamily} stickerize={true} />
                            </div>
                        ) : (
                            <div className="flex-grow"></div>
                        )}
                        <button
                            onClick={() => handleCopyCaption(post.content.text || '')}
                            className="p-1 rounded hover:bg-white/10 text-gray-400 hover:text-white transition-all flex-shrink-0 active:scale-90 mt-1 cursor-pointer"
                            title={post.type === 'text' ? "Copy Text" : "Copy Caption"}
                        >
                            {copiedText ? (
                                <span className="text-[10px] text-green-400 font-bold uppercase tracking-wider animate-pulse">Copied!</span>
                            ) : (
                                <CopyIcon className="w-3.5 h-3.5" />
                            )}
                        </button>
                    </div>
                )}
                {post.type === 'bump' && (
                    <button onClick={() => onPostClick && onPostClick(postToShow)} className="flex mb-4 p-3 w-full bg-transparent border border-white/10 rounded-lg items-center text-left hover:opacity-80 transition-opacity">
                        <img src={usersData[postToShow.author]?.pfp} alt={postToShow.author} className="w-10 h-10 rounded-full mr-2 object-cover" />
                        <div className="whitespace-pre-wrap"><FormattedText text={postToShow.type !== 'text' ? postToShow.content.text || 'Original Post' : 'Original Post'} onNavigateToProfile={handleNavigateAndClose} onNavigateToHashtag={handleNavigateHashtagAndClose} textSize={postToShow.textSize || 'med'} textColor={postTextColor} fontFamily={postFontFamily} stickerize={true} /></div>
                    </button>
                )}
                <div className="w-full max-w-md">
                    <h3 className="font-bold mb-3 text-sm" style={{ color: postTextColor, ...stickerTextStyle(0.35) }}>Comments ({post.comments.length})</h3>
                    <div className="space-y-4">
                        {(() => {
                            const renderComment = (comment: any, depth = 0) => {
                                const replies = post.comments.filter(c => c.parentId === comment.id);
                                const cleanAuthor = comment.author.startsWith('@') ? comment.author.slice(1) : comment.author;
                                const commenterKey = Object.keys(usersData).find(key => key.toLowerCase() === cleanAuthor.toLowerCase()) || cleanAuthor;
                                const commenterData = usersData[commenterKey] || {};
                                const commenterThemeColor = enforceThemeColor ? (currentUserData?.appThemeColor || '#161b22') : (commenterData.appThemeColor || '#161b22');
                                const commenterTextColor = currentUserData?.enforceTextColor ? (currentUserData.appTextColor || '#ffffff') : (commenterData.appTextColor || '#ffffff');
                                const commenterOpacity = (enforceThemeOpacity ? (currentUserData?.postBlurOpacity ?? 50) : (commenterData.postBlurOpacity ?? 50)) / 100;
                                return (
                                    <React.Fragment key={comment.id}>
                                        <div className="relative">
                                            <ChatBubble
                                                text={comment.text}
                                                author={cleanAuthor}
                                                pfp={commenterData.pfp}
                                                themeColor={commenterThemeColor}
                                                textColor={commenterTextColor}
                                                opacity={commenterOpacity}
                                                reactions={comment.reactions}
                                                currentUser={currentUser || ''}
                                                onReactionClick={(emoji) => onCommentReaction?.(post.id, comment.id, emoji)}
                                                onNavigateToProfile={handleNavigateAndClose}
                                                onNavigateToHashtag={handleNavigateHashtagAndClose}
                                                onTouchStart={(e) => handleCommentTouchStart(e, comment)}
                                                onTouchMove={handleCommentTouchMove}
                                                onTouchEnd={handleCommentTouchEnd}
                                                onMouseDown={(e) => handleCommentTouchStart(e, comment)}
                                                onMouseMove={handleCommentTouchMove}
                                                onMouseUp={handleCommentTouchEnd}
                                                onMouseLeave={handleCommentTouchEnd}
                                                onContextMenu={(e) => handleCommentContextMenu(e, comment)}
                                                depth={depth}
                                                media={comment.media}
                                            />
                                            {commentToDelete === comment.id && (
                                                <div className="absolute inset-0 bg-gray-900/95 flex items-center justify-center gap-3 z-10 animate-fade-in rounded-2xl">
                                                    <span className="text-[10px] font-bold text-gray-300">Delete?</span>
                                                    <button onClick={() => { onDeleteComment && onDeleteComment(post.id, comment.id); setCommentToDelete(null); }} className="bg-red-600 text-white text-[9px] font-bold py-1 px-3 rounded-full">Confirm</button>
                                                    <button onClick={() => setCommentToDelete(null)} className="bg-gray-700 text-white text-[9px] font-bold py-1 px-3 rounded-full">Cancel</button>
                                                </div>
                                            )}
                                        </div>
                                        {replies.length > 0 && (
                                            <div className="mobile-replies-container w-full overflow-x-hidden">
                                                {replies.map(reply => renderComment(reply, depth + 1))}
                                            </div>
                                        )}
                                    </React.Fragment>
                                );
                            };
                            const rootComments = post.comments.filter(c => !c.parentId);
                            return rootComments.length > 0 ? rootComments.map(c => renderComment(c)) : (
                                <p className="text-gray-400 text-xs text-center py-6">No comments yet. Go ahead, speak your mind!</p>
                            );
                        })()}
                    </div>
                </div>
            </div>

            <div className="flex-shrink-0 p-4 space-y-3">
                {post.linkRequestDetails?.isLinkRequest ? (
                    mediaItems.length === 0 ? (
                        <div className="mb-2">
                            <LinkRequestRSVPSection
                                post={post}
                                currentUser={currentUser}
                                usersData={usersData}
                                postThemeColor={currentUserData?.appThemeColor || '#3b82f6'}
                                onRespondLinkRequest={onRespondLinkRequest}
                            />
                        </div>
                    ) : null
                ) : (
                    <div className="flex items-center space-x-4 justify-center text-sm" style={{ color: postTextColor }}>
                        {post.footprintDetails ? (
                            <button className="flex items-center gap-1 hover:opacity-80 transition-transform hover:scale-105 active:scale-95" onClick={() => onVouch && onVouch(post.id)}>
                                <CheckmarkIcon isVouched={post.vouches?.some(v => v.userId === currentUser)} className={`w-5 h-5 ${post.vouches?.some(v => v.userId === currentUser) ? 'text-violet-500' : ''}`} style={!post.vouches?.some(v => v.userId === currentUser) ? { color: postTextColor } : {}} />
                                <span>{post.vouches?.length || 0}</span>
                            </button>
                        ) : (
                            <button className="flex items-center gap-1 hover:opacity-80 transition-transform hover:scale-105 active:scale-95" onClick={() => onLike(post.id)}>
                                <LikeIcon isLiked={post.isLiked} className={`w-5 h-5 ${post.isLiked ? 'text-red-500' : ''} ${animatingLikes.has(post.id) ? 'animate-heart-burst' : ''}`} style={!post.isLiked ? { color: postTextColor } : {}} />
                                <span>{post.likes}</span>
                            </button>
                        )}
                        <button className="flex items-center gap-1 hover:opacity-80 transition-transform hover:scale-105 active:scale-95" onClick={() => onBump(postToShow)}>
                            <BumpIcon isBumped={postToShow.isBumped} className={`w-5 h-5 ${postToShow.isBumped ? 'text-emerald-500' : ''} ${animatingBumps.has(postToShow.id) ? 'animate-bump-spin' : ''}`} style={!postToShow.isBumped ? { color: postTextColor } : {}} />
                            <span>{postToShow.bumps}</span>
                        </button>
                        <button className="flex items-center gap-1 hover:opacity-80 transition-transform hover:scale-105 active:scale-95" onClick={() => onShare && onShare(post)}>
                            <SendIcon className={`w-5 h-5`} style={{ color: postTextColor }} />
                            <span>{post.shares || 0}</span>
                        </button>
                        <button className="flex items-center gap-1 hover:opacity-80 transition-transform hover:scale-105 active:scale-95" onClick={() => onBookmark(post.id)}>
                            <BookmarkIcon isBookmarked={post.isBookmarked} className={`w-5 h-5 ${post.isBookmarked ? 'text-blue-500' : ''} ${animatingBookmarks.has(post.id) ? 'animate-bookmark-pop' : ''}`} style={!post.isBookmarked ? { color: postTextColor } : {}} />
                            <span>{currentUser === post.author ? post.bookmarks : ''}</span>
                        </button>
                    </div>
                )}
                {attachments.length > 0 && (
                    <div className="flex flex-wrap gap-2 pb-2">
                        {attachments.map((at, idx) => (
                            <div key={idx} className="relative group flex-shrink-0">
                                <div className={`${(at.type === 'audio' || at.type === 'voice') ? 'w-full' : 'w-12'} h-12 rounded-xl overflow-hidden bg-black/20`}>
                                    {at.type === 'image' && <img src={at.url} className="w-full h-full object-cover" alt="" />}
                                    {at.type === 'video' && <video src={`${at.url}#t=0.001`} className="w-full h-full object-cover" muted playsInline />}
                                    {(at.type === 'audio' || at.type === 'voice') && (
                                        <div className="p-1 min-w-[120px]">
                                            <CustomAudioPlayer src={at.url} />
                                        </div>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    onClick={() => removeAttachment(idx)}
                                    className="absolute -top-1.5 -right-1.5 bg-red-600 text-white rounded-full p-0.5 shadow-lg flex items-center justify-center hover:bg-red-500 transition-colors"
                                >
                                    <CloseIcon className="w-3 h-3" />
                                </button>
                            </div>
                        ))}
                    </div>
                )}
                {showMessageRecorder && (
                    <div className="bg-gray-900/80 backdrop-blur-sm rounded-xl px-4 py-2 border border-blue-500/30 min-h-[48px] flex items-center mb-1">
                        <AudioRecorder
                            ref={audioRecorderRef}
                            onRecordingComplete={handleVoiceMessageComplete}
                            onReset={handleVoiceMessageReset}
                            autoStart={true}
                            showControls={false}
                            stream={audioStream}
                        />
                    </div>
                )}
                <form onSubmit={handleCommentSubmit} className="flex items-center gap-3">
                    <img src={usersData[currentUser || '']?.pfp} alt="Your profile" className="w-9 h-9 rounded-full flex-shrink-0 object-cover" />
                    <div className="flex-grow relative flex items-center bg-gray-800 rounded-full border border-white/5 focus-within:ring-2 focus-within:ring-blue-500 transition-all">
                        <div className="flex-none relative pl-2">
                            <button
                                type="button"
                                onClick={() => setShowInputMenu(!showInputMenu)}
                                className={`p-2 rounded-xl transition-all ${showInputMenu ? 'bg-white/10 text-white' : 'text-gray-400 hover:bg-white/5 hover:text-white'}`}
                                title="More options"
                            >
                                <PlusIcon className={`w-5 h-5 transition-transform duration-200 ${showInputMenu ? 'rotate-45' : ''}`} />
                            </button>
                            {showInputMenu && (
                                <>
                                    <div className="fixed inset-0 z-40" onClick={() => setShowInputMenu(false)} />
                                    <div className="absolute bottom-full left-0 mb-2 bg-[#1b1b1f] border border-white/10 rounded-full shadow-2xl overflow-hidden flex flex-row items-center p-1 animate-fade-in z-50">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setShowInputMenu(false);
                                                const isAndroid = Capacitor.getPlatform() === 'android' || (typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent));
                                                if (isAndroid) {
                                                    setCameraMode('image');
                                                    setShowCamera(true);
                                                } else {
                                                    setCameraMode('both');
                                                    setShowCamera(true);
                                                }
                                            }}
                                            className="p-2.5 hover:bg-white/5 rounded-full transition-colors text-white"
                                            title="Camera"
                                        >
                                            <CameraIcon className="w-5 h-5 text-blue-400" />
                                        </button>
                                        {(() => {
                                            const isAndroid = Capacitor.getPlatform() === 'android' || (typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent));
                                            if (isAndroid) {
                                                return (
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setShowInputMenu(false);
                                                            setCameraMode('video');
                                                            setShowCamera(true);
                                                        }}
                                                        className="p-2.5 hover:bg-white/5 rounded-full transition-colors text-white"
                                                        title="Video"
                                                    >
                                                        <VideoIcon className="w-5 h-5 text-purple-400" />
                                                    </button>
                                                );
                                            }
                                            return null;
                                        })()}
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setShowInputMenu(false);
                                                handleGalleryClick();
                                            }}
                                            className="p-2.5 hover:bg-white/5 rounded-full transition-colors text-white"
                                            title="Gallery"
                                        >
                                            <PaperclipIcon className="w-5 h-5 text-green-400" />
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setShowInputMenu(false);
                                                setShowTenorModal(true);
                                            }}
                                            className="p-2.5 hover:bg-white/5 rounded-full transition-colors text-white"
                                            title="GIF"
                                        >
                                            <span className="text-[10px] font-black tracking-widest px-1.5 py-1 rounded bg-pink-500/20 text-pink-400 border border-pink-500/30 leading-none flex items-center justify-center h-5">GIF</span>
                                        </button>
                                        <button
                                            type="button"
                                            onClick={async () => {
                                                setShowInputMenu(false);
                                                if (!showMessageRecorder) {
                                                    try {
                                                        await requestMicrophonePermission();
                                                        const stream = await navigator.mediaDevices.getUserMedia({
                                                            audio: {
                                                                echoCancellation: false,
                                                                noiseSuppression: true,
                                                                autoGainControl: false,
                                                                sampleRate: 48000,
                                                                sampleSize: 16,
                                                                channelCount: 1
                                                            }
                                                        });
                                                        setAudioStream(stream);
                                                        setShowMessageRecorder(true);
                                                    } catch (err) {
                                                        console.error("Microphone access failed:", err);
                                                    }
                                                } else {
                                                    audioRecorderRef.current?.stopRecording();
                                                    setShowMessageRecorder(false);
                                                }
                                            }}
                                            className={`p-2.5 hover:bg-white/5 rounded-full transition-colors ${showMessageRecorder ? 'text-red-400' : 'text-white'}`}
                                            title="Voice"
                                        >
                                            <MicIcon className={`w-5 h-5 ${showMessageRecorder ? 'text-red-400 animate-pulse' : 'text-red-400'}`} />
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setShowInputMenu(false);
                                                setShowEmojiPickerFor('comment-input');
                                            }}
                                            className="p-2.5 hover:bg-white/5 rounded-full transition-colors text-white"
                                            title="Emoji"
                                        >
                                            <EmojiIcon className="w-5 h-5 text-yellow-400" />
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                        <textarea
                            value={newComment}
                            onChange={(e) => {
                                const val = e.target.value;
                                setNewComment(val);
                                updateSearch(val, e.target.selectionStart);
                            }}
                            onKeyDown={handleCommentKeyDown}
                            onPaste={handlePaste}
                            placeholder="What you got to say?"
                            className="flex-1 bg-transparent py-2.5 pl-2 pr-12 focus:outline-none resize-none text-sm transition-all"
                            rows={1}
                            style={{ minHeight: '40px', maxHeight: '100px', overflow: 'auto' }}
                            disabled={isUploadingMedia}
                        />
                        {mentionQuery !== null && (
                            <MentionDropdown
                                query={mentionQuery}
                                following={currentUserData?.following || []}
                                usersData={usersData}
                                onSelect={(username) => {
                                    const { newText, newCursorPos } = completeMention(newComment, username, newComment.length);
                                    setNewComment(newText);
                                    setTimeout(() => {
                                        const el = document.querySelector('textarea');
                                        if (el) {
                                            el.focus();
                                            el.setSelectionRange(newCursorPos, newCursorPos);
                                        }
                                    }, 10);
                                }}
                                position="above"
                            />
                        )}
                        <button type="submit" className="absolute right-1.5 p-2 text-white rounded-full hover:bg-white/10 transition-all disabled:opacity-50 bg-gray-600 shadow-sm" disabled={(!newComment.trim() && attachments.length === 0) || isUploadingMedia}>
                            {isUploadingMedia ? <span className="animate-spin inline-block w-4 h-4 border-2 border-t-transparent border-current rounded-full" /> : <CommentIcon className="w-4 h-4" />}
                        </button>
                    </div>
                    <input type="file" id="comment-file-mobile" ref={commentFileInputRef} multiple hidden onChange={handleMediaUpload} accept="*/*" />
                </form>
            </div>
        </div>
    );

    return (
        <div
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 animate-fade-in md:flex"
            onClick={onClose}
            onTouchStart={handleModalTouchStart}
            onTouchEnd={handleModalTouchEnd}
            style={{ ['--theme-color' as any]: postColorRgb ? `rgba(${postColorRgb}, 1)` : '' }}
        >
            {!isMobilePortrait ? (
                <div
                    key={post.id}
                    className={`flex w-full h-full shadow-lg overflow-hidden
                        ${isMobileLandscape ? 'max-w-full max-h-[100dvh] rounded-none' : 'hidden md:flex max-w-6xl max-h-[90dvh] rounded-2xl'}
                        ${modalSwipeDirection === 'left' ? 'page-slide-left' : ''}
                        ${modalSwipeDirection === 'right' ? 'page-slide-right' : ''}
                    `}
                    style={{ backgroundColor: modalBg }}
                    onClick={(e) => e.stopPropagation()}
                    onAnimationEnd={() => setModalSwipeDirection(null)}
                >
                    {desktopView}
                </div>
            ) : (
                <div
                    key={post.id}
                    className={`w-full h-[100dvh] md:hidden
                        ${modalSwipeDirection === 'left' ? 'page-slide-left' : ''}
                        ${modalSwipeDirection === 'right' ? 'page-slide-right' : ''}
                    `}
                    onClick={(e) => e.stopPropagation()}
                    onAnimationEnd={() => setModalSwipeDirection(null)}
                >
                    {mobileView}
                </div>
            )}

            {isArchiveModalOpen && (
                <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[80] animate-fade-in" onClick={(e) => { e.stopPropagation(); setIsArchiveModalOpen(false); }}>
                    <div className="rounded-2xl shadow-lg p-8 max-w-md w-full mx-4 text-center backdrop-blur-md" style={{ backgroundColor: modalBg }} onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-center mb-4">
                            <BookmarkIcon className="w-16 h-16 text-yellow-500" />
                        </div>
                        <h2 className="text-2xl font-bold mb-4">Archive Post?</h2>
                        <p className="text-gray-300 mb-6">Are you sure you want to archive this post? It will be removed from your profile and feeds, but you can view or unarchive it later from the Archive tab.</p>
                        <div className="flex flex-col gap-4">
                            <button
                                onClick={() => {
                                    if (onArchive) onArchive(post.id);
                                    setIsArchiveModalOpen(false);
                                }}
                                className="bg-yellow-600 hover:bg-yellow-700 text-white font-bold py-3 px-6 rounded-full transition-colors w-full"
                            >
                                Archive Post
                            </button>
                            <button onClick={() => setIsArchiveModalOpen(false)} className="text-gray-400 hover:text-white font-bold py-3 px-6 rounded-full transition-colors w-full">Cancel</button>
                        </div>
                    </div>
                </div>
            )}
            {activeCommentMenu && (
                <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm shadow-2xl" onClick={(e) => { e.stopPropagation(); setActiveCommentMenu(null); }}>
                    <div className="w-full max-w-xs bg-[#1b1b1f] border border-white/10 rounded-3xl overflow-hidden p-2" onClick={e => e.stopPropagation()}>
                        <button
                            onClick={() => {
                                setReplyTo(activeCommentMenu);
                                setEditingComment(null);
                                setActiveCommentMenu(null);
                                setTimeout(() => {
                                    const el = document.querySelector('.custom-comment-textarea') as HTMLTextAreaElement;
                                    if (el) el.focus();
                                }, 100);
                            }}
                            className="w-full flex items-center gap-3 p-3 hover:bg-white/5 rounded-2xl text-sm transition-colors text-white text-left"
                        >
                            <ReplyIcon className="w-4 h-4 text-blue-400" />
                            <span>Reply</span>
                        </button>
                        <button
                            onClick={() => {
                                navigator.clipboard.writeText(activeCommentMenu.text || '');
                                setActiveCommentMenu(null);
                            }}
                            className="w-full flex items-center gap-3 p-3 hover:bg-white/5 rounded-2xl text-sm transition-colors text-white text-left"
                        >
                            <CopyIcon className="w-4 h-4 text-green-400" />
                            <span>Copy Text</span>
                        </button>
                        {activeCommentMenu.media && activeCommentMenu.media.length > 0 && (
                            <button
                                onClick={() => {
                                    const firstMedia = activeCommentMenu.media?.[0];
                                    if (firstMedia) downloadMediaUrl(firstMedia.url, firstMedia.file?.name || `${firstMedia.type}-attachment`);
                                    setActiveCommentMenu(null);
                                }}
                                className="w-full flex items-center gap-3 p-3 hover:bg-white/5 rounded-2xl text-sm transition-colors text-white text-left"
                            >
                                <DownloadIcon className="w-4 h-4 text-cyan-400" />
                                <span>Save Media</span>
                            </button>
                        )}
                        {activeCommentMenu.author === currentUser && (
                            <>
                                <button
                                    onClick={() => {
                                        setEditingComment(activeCommentMenu);
                                        setNewComment(activeCommentMenu.text || '');
                                        setReplyTo(null);
                                        setActiveCommentMenu(null);
                                        setTimeout(() => {
                                            const el = document.querySelector('.custom-comment-textarea') as HTMLTextAreaElement;
                                            if (el) el.focus();
                                        }, 100);
                                    }}
                                    className="w-full flex items-center gap-3 p-3 hover:bg-white/5 rounded-2xl text-sm transition-colors text-white text-left"
                                >
                                    <EditIcon className="w-4 h-4 text-orange-400" />
                                    <span>Edit Comment</span>
                                </button>
                                <button
                                    onClick={() => {
                                        setCommentToDelete(activeCommentMenu.id);
                                        setActiveCommentMenu(null);
                                    }}
                                    className="w-full flex items-center gap-3 p-3 hover:bg-red-500/10 text-red-400 rounded-2xl text-sm transition-colors text-left"
                                >
                                    <TrashIcon className="w-4 h-4" />
                                    <span>Delete</span>
                                </button>
                            </>
                        )}
                    </div>
                </div>
            )}

            {showEmojiPickerFor === 'comment-input' && (
                <EmojiPickerModal
                    isOpen={true}
                    onClose={() => setShowEmojiPickerFor(null)}
                    onEmojiSelect={(emoji: string) => {
                        setNewComment(prev => prev + emoji);
                        setShowEmojiPickerFor(null);
                    }}
                />
            )}

            <GifPickerModal
                isOpen={showTenorModal}
                onClose={() => setShowTenorModal(false)}
                onSelectGif={(url) => handleAttachTenorGif(url)}
                title="Search GIFs"
                zIndexClass="z-[150]"
            />

            <NativeCamera
                isOpen={showCamera}
                onClose={() => setShowCamera(false)}
                onCapture={handleCameraCapture}
                onOpenGallery={handleGalleryClick}
                captureMode={cameraMode}
            />

        </div>
    );
};

export const BumpActionModal = ({ isOpen, onClose, onConfirm, onRemove }: { isOpen: boolean, onClose: () => void, onConfirm: () => void, onRemove: () => void }) => {
    if (!isOpen) return null;
    return (
        <div
            className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[60] animate-fade-in"
            onClick={onClose}
            onTouchStart={(e) => e.stopPropagation()}
            onTouchMove={(e) => e.stopPropagation()}
            onTouchEnd={(e) => e.stopPropagation()}
        >
            <div className="rounded-2xl shadow-lg p-8 max-w-md w-full mx-4 text-center backdrop-blur-md" style={{ backgroundColor: 'var(--app-theme-bg)' }} onClick={(e) => e.stopPropagation()}>
                <h2 className="text-2xl font-bold mb-4">Bump Post</h2>
                <p className="text-gray-300 mb-6">You've already bumped this. What would you like to do?</p>
                <div className="flex flex-col gap-4">
                    <button onClick={onConfirm} className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-full transition-colors w-full">Bump Again</button>
                    <button onClick={onRemove} className="border border-red-500 text-red-500 hover:bg-red-500 hover:text-white font-bold py-3 px-6 rounded-full transition-colors w-full">Unbump</button>
                    <button onClick={onClose} className="text-gray-400 hover:text-white font-bold py-3 px-6 rounded-full transition-colors w-full">Cancel</button>
                </div>
            </div>
        </div>
    );
};

export const BumpModal = ({ isOpen, onClose, onConfirm, onNavigateToProfile, onNavigateToHashtag, post, currentUserData, usersData }: { isOpen: boolean, onClose: () => void, onConfirm: (caption: string) => void, onNavigateToProfile: (userId: string) => void, onNavigateToHashtag: (tag: string) => void, post: HydratedPost | null, currentUserData: UserData | null, usersData: Record<string, UserData> }) => {
    const [caption, setCaption] = useState('');
    const { mentionQuery, updateSearch, completeMention } = useMentionSearch();
    useEffect(() => { if (!isOpen) setCaption(''); }, [isOpen]);
    if (!isOpen || !post) return null;
    return (
        <div
            className="fixed inset-0 bg-black/80 backdrop-blur-lg flex items-center justify-center z-[70]"
            onClick={onClose}
            onTouchStart={(e) => e.stopPropagation()}
            onTouchMove={(e) => e.stopPropagation()}
            onTouchEnd={(e) => e.stopPropagation()}
        >
            <div className="rounded-2xl shadow-lg p-8 max-w-xl w-full mx-4 backdrop-blur-md" style={{ backgroundColor: 'var(--app-theme-bg)' }} onClick={(e) => e.stopPropagation()}>
                <h2 className="text-2xl font-bold mb-4">Add to your post</h2>
                <div className="relative">
                    <textarea
                        className="w-full bg-gray-800/50 p-3 text-lg text-gray-200 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-lg resize-y min-h-[100px]"
                        value={caption}
                        onChange={(e) => {
                            const val = e.target.value;
                            setCaption(val);
                            updateSearch(val, e.target.selectionStart);
                        }}
                        placeholder="Add a caption..."
                        aria-label="Bump caption"
                    />
                    {mentionQuery !== null && (
                        <MentionDropdown
                            query={mentionQuery}
                            following={currentUserData?.following || []}
                            usersData={usersData}
                            onSelect={(username) => {
                                const { newText, newCursorPos } = completeMention(caption, username, caption.length);
                                setCaption(newText);
                                setTimeout(() => {
                                    const el = document.querySelector('textarea[aria-label="Bump caption"]') as HTMLTextAreaElement;
                                    if (el) {
                                        el.focus();
                                        el.setSelectionRange(newCursorPos, newCursorPos);
                                    }
                                }, 10);
                            }}
                            className="bottom-full left-0 mb-2"
                        />
                    )}
                </div>
                <div className="mt-4"><BumpedPostContent post={post} onNavigateToProfile={onNavigateToProfile} onNavigateToHashtag={onNavigateToHashtag} /></div>
                <div className="flex justify-end gap-4 mt-6">
                    <button onClick={onClose} className="text-gray-400 hover:text-white font-bold py-2 px-6 rounded-full transition-colors">Cancel</button>
                    <button onClick={() => onConfirm(caption)} className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded-full transition-colors">Bump</button>
                </div>
            </div>
        </div>
    );
};

export const DeleteConfirmationModal = ({ isOpen, onClose, onConfirm }: { isOpen: boolean, onClose: () => void, onConfirm: () => void }) => {
    if (!isOpen) return null;
    return (
        <div
            className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[70]"
            onClick={onClose}
            onTouchStart={(e) => e.stopPropagation()}
            onTouchMove={(e) => e.stopPropagation()}
            onTouchEnd={(e) => e.stopPropagation()}
        >
            <div className="rounded-2xl shadow-lg p-8 max-w-md w-full mx-4 text-center backdrop-blur-md" style={{ backgroundColor: 'var(--app-theme-bg)' }} onClick={(e) => e.stopPropagation()}>
                <h2 className="text-2xl font-bold mb-4 text-red-500">Delete Post</h2>
                <p className="text-gray-300 mb-6">Are you sure you want to permanently delete this post? This action cannot be undone.</p>
                <div className="flex justify-center gap-4">
                    <button onClick={onClose} className="text-gray-400 hover:text-white font-bold py-2 px-6 rounded-full transition-colors">Cancel</button>
                    <button onClick={onConfirm} className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-6 rounded-full transition-colors">Delete</button>
                </div>
            </div>
        </div>
    );
};
